// App.jsx — Content Command Center (zenithpost.ai)
// CHANGES vs previous version:
//   1. sanitizeErrorMessage + SAFE_ERROR_MESSAGES exported — used here and
//      imported by PipelineQueue so infra errors NEVER reach the UI anywhere.
//   2. On mount, fetches /pipeline/tasks from MongoDB and merges into
//      queueTasks so the queue survives logout/refresh/browser close.
//      sessionStorage tasks take priority (they have fresher status).
//      PipelineQueue's polling loop auto-resumes for any active restored tasks.
//   3. handleTaskFailed sanitizes the raw error string before showing a banner.
//   4. parseApiError catches all 4xx/5xx and strips infra details from 500s.

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import {
  ThemeProvider, createTheme, CssBaseline, AppBar, Toolbar, Typography,
  Container, TextField, Button, Grid, Card, CardContent, Tabs, Tab, Box,
  Table, TableHead, TableRow, TableCell, TableBody, Collapse, Divider,
  TableContainer, Chip, Menu, MenuItem, Avatar, Drawer, List, ListItem,
  ListItemButton, ListItemText, useMediaQuery, IconButton, Tooltip,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import { getEmailFromToken } from "./auth";
import PipelineQueue from "./Pipelinequeue";

// ─── THEME ────────────────────────────────────────────────────────────────────
const muiTheme = createTheme({
  palette: {
    mode: "dark",
    primary:    { main: "#6c7ef8" },
    secondary:  { main: "#34d399" },
    background: { default: "#0d0e14", paper: "#13151e" },
    text:       { primary: "#f0f2f7", secondary: "#b0b7ce" },
  },
  typography: { fontFamily: "'DM Sans', system-ui, sans-serif" },
});

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const D = {
  bg:           "#0d0e14",
  surface:      "#13151e",
  surfaceUp:    "#191c28",
  border:       "rgba(255,255,255,0.07)",
  borderMed:    "rgba(255,255,255,0.11)",
  accent:       "#6c7ef8",
  accentSoft:   "rgba(108,126,248,0.1)",
  accentBorder: "rgba(108,126,248,0.2)",
  success:      "#34d399",
  successSoft:  "rgba(52,211,153,0.1)",
  warn:         "#f59e0b",
  warnSoft:     "rgba(245,158,11,0.1)",
  danger:       "#f87171",
  dangerSoft:   "rgba(248,113,113,0.1)",
  purple:       "#a37bf8",
  textPrimary:  "#f0f2f7",
  textSec:      "#b0b7ce",
  textMuted:    "#6b7390",
  fontDisplay:  "'Plus Jakarta Sans', 'DM Sans', sans-serif",
  fontBody:     "'DM Sans', sans-serif",
  fontMono:     "'JetBrains Mono', monospace",
};

// ─── GLOBAL STYLES ────────────────────────────────────────────────────────────
const G = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; }
  body { background: #0d0e14; color: #f0f2f7; font-family: 'DM Sans', sans-serif; -webkit-font-smoothing: antialiased; overflow-x: hidden; }
  ::selection { background: rgba(108,126,248,0.25); }
  ::-webkit-scrollbar { width: 5px; height: 5px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 99px; transition: background 0.2s; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.2); }

  @keyframes fadeUp      { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  @keyframes fadeUpBlur  { from { opacity:0; transform:translateY(22px); filter:blur(6px); } to { opacity:1; transform:translateY(0); filter:blur(0px); } }
  @keyframes fadeIn      { from{opacity:0} to{opacity:1} }
  @keyframes spin        { to { transform:rotate(360deg); } }
  @keyframes shimmer     { 0% { background-position: -800px 0; } 100% { background-position: 800px 0; } }
  @keyframes pulse       { 0%,100%{opacity:1} 50%{opacity:0.35} }
  @keyframes slideInDown { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes scaleIn     { from { opacity:0; transform:scale(0.94) translateY(10px); } to { opacity:1; transform:scale(1) translateY(0); } }
  @keyframes logoGlow    { 0%,100% { box-shadow: 0 0 20px rgba(108,126,248,0.3), 0 4px 16px rgba(108,126,248,0.2); } 50% { box-shadow: 0 0 45px rgba(163,123,248,0.55), 0 4px 24px rgba(108,126,248,0.35); } }
  @keyframes float       { 0%,100%{ transform:translateY(0px); } 50%{ transform:translateY(-7px); } }
  @keyframes orbFloat1   { 0%,100%{ transform:translate(0,0) scale(1); } 33%{ transform:translate(45px,-35px) scale(1.08); } 66%{ transform:translate(-22px,22px) scale(0.95); } }
  @keyframes orbFloat2   { 0%,100%{ transform:translate(0,0) scale(1); } 33%{ transform:translate(-38px,28px) scale(1.06); } 66%{ transform:translate(28px,-18px) scale(0.97); } }
  @keyframes orbFloat3   { 0%,100%{ transform:translate(0,0); } 50%{ transform:translate(20px,-45px); } }
  @keyframes glowBreath  { 0%,100%{ opacity:0.6; } 50%{ opacity:1; } }
  @keyframes shimmerSweep { 0%{ transform:translateX(-200%); } 100%{ transform:translateX(200%); } }
  @keyframes progressGlow { 0%,100%{ box-shadow:0 0 6px rgba(108,126,248,0.4); } 50%{ box-shadow:0 0 16px rgba(108,126,248,0.7), 0 0 30px rgba(163,123,248,0.3); } }
  @keyframes ringExpand  { 0%{ box-shadow:0 0 0 0 rgba(108,126,248,0.4); } 70%{ box-shadow:0 0 0 10px rgba(108,126,248,0); } 100%{ box-shadow:0 0 0 0 rgba(108,126,248,0); } }
  @keyframes loadOrbit   { to { transform:rotate(360deg); } }
  @keyframes loadDot     { 0%,80%,100%{ transform:scale(0.6); opacity:0.3; } 40%{ transform:scale(1); opacity:1; } }

  .fade-up   { animation: fadeUpBlur 0.52s cubic-bezier(0.22,1,0.36,1) both; }
  .fade-up-1 { animation-delay:0.07s; }
  .fade-up-2 { animation-delay:0.14s; }
  .fade-up-3 { animation-delay:0.21s; }
  .fade-up-4 { animation-delay:0.28s; }

  /* ── Animated background orbs ── */
  .page-glow {
    position:fixed; inset:0; z-index:0; pointer-events:none; overflow:hidden;
  }
  .page-glow::before {
    content:''; position:absolute; width:800px; height:800px; border-radius:50%;
    background:radial-gradient(circle, rgba(108,126,248,0.07) 0%, transparent 68%);
    top:-250px; left:-220px;
    animation:orbFloat1 22s ease-in-out infinite;
    will-change:transform;
  }
  .page-glow::after {
    content:''; position:absolute; width:650px; height:650px; border-radius:50%;
    background:radial-gradient(circle, rgba(52,211,153,0.045) 0%, transparent 68%);
    bottom:-180px; right:-180px;
    animation:orbFloat2 28s ease-in-out infinite;
    will-change:transform;
  }
  .page-glow-mid {
    position:fixed; width:500px; height:500px; border-radius:50%; z-index:0; pointer-events:none;
    background:radial-gradient(circle, rgba(163,123,248,0.03) 0%, transparent 70%);
    top:50%; left:50%; transform:translate(-50%,-50%);
    animation:orbFloat3 32s ease-in-out infinite;
    will-change:transform;
  }

  /* ── MUI overrides ── */
  .MuiOutlinedInput-root { border-radius:11px !important; background:rgba(255,255,255,0.03) !important; font-family:'DM Sans',sans-serif !important; font-size:14px !important; transition:box-shadow 0.25s ease !important; }
  .MuiOutlinedInput-notchedOutline { border-color:rgba(255,255,255,0.09) !important; transition:border-color 0.25s ease !important; }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline { border-color:rgba(255,255,255,0.18) !important; }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline { border-color:#6c7ef8 !important; border-width:1.5px !important; }
  .MuiOutlinedInput-root.Mui-focused { box-shadow:0 0 0 4px rgba(108,126,248,0.13), 0 4px 18px rgba(108,126,248,0.1) !important; }
  .MuiInputLabel-root { font-family:'DM Sans',sans-serif !important; color:#8b91a8 !important; transition:color 0.2s ease !important; }
  .MuiInputLabel-root.Mui-focused { color:#6c7ef8 !important; }
  .MuiButton-root { border-radius:10px !important; text-transform:none !important; font-family:'DM Sans',sans-serif !important; font-weight:600 !important; transition:all 0.22s cubic-bezier(0.34,1.56,0.64,1) !important; }
  .MuiButton-contained:hover { transform:translateY(-2px) scale(1.01) !important; }
  .MuiButton-contained:active { transform:scale(0.97) !important; }
  .MuiCard-root { border-radius:16px !important; background:#13151e !important; border:1px solid rgba(255,255,255,0.07) !important; box-shadow:0 4px 20px rgba(0,0,0,0.3) !important; transition:border-color 0.3s ease, box-shadow 0.35s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1) !important; }
  .MuiCard-root:hover { border-color:rgba(255,255,255,0.12) !important; box-shadow:0 8px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(108,126,248,0.06) !important; transform:translateY(-2px) !important; }
  .MuiTab-root { text-transform:none !important; font-family:'DM Sans',sans-serif !important; font-weight:500 !important; font-size:13.5px !important; min-height:44px !important; color:#b0b7ce !important; transition:color 0.2s ease !important; }
  .MuiTab-root.Mui-selected { color:#f0f2f7 !important; font-weight:600 !important; }
  .MuiTabs-indicator { background:linear-gradient(90deg, #6c7ef8, #a37bf8) !important; height:2px !important; border-radius:2px !important; transition:all 0.35s cubic-bezier(0.34,1.56,0.64,1) !important; }
  .MuiTableCell-head { background:rgba(255,255,255,0.02) !important; font-family:'JetBrains Mono',monospace !important; font-size:10.5px !important; font-weight:600 !important; letter-spacing:0.06em !important; color:#8b97b8 !important; text-transform:uppercase !important; border-bottom:1px solid rgba(255,255,255,0.07) !important; padding:10px 16px !important; white-space:nowrap !important; }
  .MuiTableCell-body { font-family:'DM Sans',sans-serif !important; font-size:13.5px !important; border-bottom:1px solid rgba(255,255,255,0.05) !important; color:#d0d5e8 !important; padding:12px 16px !important; transition:color 0.15s ease !important; }
  .MuiChip-root { font-family:'DM Sans',sans-serif !important; font-size:12px !important; font-weight:600 !important; border-radius:6px !important; transition:all 0.2s ease !important; }
  .MuiTooltip-tooltip { background:#1f2230 !important; border:1px solid rgba(255,255,255,0.1) !important; border-radius:8px !important; font-family:'DM Sans',sans-serif !important; font-size:12px !important; color:#f0f2f7 !important; padding:6px 10px !important; max-width:360px !important; white-space:pre-wrap !important; line-height:1.5 !important; }
  .MuiTooltip-arrow { color:#1f2230 !important; }
  .MuiMenuItem-root { font-family:'DM Sans',sans-serif !important; font-size:13.5px !important; transition:background 0.15s ease !important; }
  .MuiPaper-root { background:#1a1d2b !important; border:1px solid rgba(255,255,255,0.08) !important; border-radius:12px !important; }

  /* ── Component classes ── */
  .stat-card {
    border:1px solid rgba(255,255,255,0.07); border-radius:14px; padding:20px 22px;
    background:#13151e; position:relative; overflow:hidden;
    transition:border-color 0.3s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease;
  }
  .stat-card::before {
    content:''; position:absolute; inset:0; border-radius:14px;
    background:linear-gradient(135deg, rgba(108,126,248,0.05) 0%, transparent 55%);
    opacity:0; transition:opacity 0.3s ease; pointer-events:none;
  }
  .stat-card:hover {
    border-color:rgba(108,126,248,0.28);
    transform:translateY(-4px);
    box-shadow:0 16px 42px rgba(0,0,0,0.38), 0 0 0 1px rgba(108,126,248,0.1), 0 0 32px rgba(108,126,248,0.07);
  }
  .stat-card:hover::before { opacity:1; }

  .topic-row { cursor:pointer; transition:background 0.18s ease; }
  .topic-row:hover { background:rgba(255,255,255,0.03) !important; }
  .topic-row.selected { background:rgba(108,126,248,0.07) !important; }
  .topic-row.selected td { color:#e0e4f6 !important; }

  .score-badge {
    display:inline-flex; align-items:center; justify-content:center;
    min-width:40px; height:22px; border-radius:5px; padding:0 6px;
    font-size:12px; font-weight:600; font-family:'JetBrains Mono',monospace;
    transition:transform 0.2s cubic-bezier(0.34,1.56,0.64,1);
  }
  .score-badge:hover { transform:scale(1.08); }

  .content-post {
    background:rgba(255,255,255,0.025); border:1px solid rgba(255,255,255,0.07);
    border-radius:11px; padding:16px 18px; margin-bottom:10px;
    position:relative; overflow:hidden;
    transition:border-color 0.25s ease, transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s ease;
  }
  .content-post::before {
    content:''; position:absolute; top:0; left:0; right:0; height:1px;
    background:linear-gradient(90deg, transparent 10%, rgba(108,126,248,0.5) 50%, transparent 90%);
    opacity:0; transition:opacity 0.25s ease;
  }
  .content-post::after {
    content:''; position:absolute; inset:0; border-radius:11px;
    background:linear-gradient(135deg, rgba(108,126,248,0.03) 0%, transparent 55%);
    opacity:0; transition:opacity 0.25s ease; pointer-events:none;
  }
  .content-post:hover {
    border-color:rgba(108,126,248,0.22);
    transform:translateY(-2px);
    box-shadow:0 8px 28px rgba(0,0,0,0.25), 0 0 22px rgba(108,126,248,0.07);
  }
  .content-post:hover::before { opacity:1; }
  .content-post:hover::after  { opacity:1; }

  .post-index { position:absolute; top:12px; right:14px; font-family:'JetBrains Mono',monospace; font-size:10.5px; color:rgba(255,255,255,0.22); font-weight:500; }

  .progress-shimmer {
    height:2px; border-radius:99px; position:relative; overflow:hidden;
    background:rgba(108,126,248,0.15);
  }
  .progress-shimmer::after {
    content:''; position:absolute; inset:0;
    background:linear-gradient(90deg, transparent 0%, #6c7ef8 30%, #a37bf8 60%, #6c7ef8 80%, transparent 100%);
    background-size:300% 100%; animation:shimmer 1.6s ease infinite;
  }

  .zip-item {
    display:flex; align-items:center; gap:10px; padding:10px 14px; border-radius:10px;
    background:rgba(255,255,255,0.03); border:1px solid rgba(255,255,255,0.08);
    cursor:pointer; position:relative; overflow:hidden;
    transition:all 0.3s cubic-bezier(0.34,1.56,0.64,1);
  }
  .zip-item::before {
    content:''; position:absolute; inset:0; border-radius:10px;
    background:linear-gradient(135deg, rgba(108,126,248,0.07) 0%, transparent 60%);
    opacity:0; transition:opacity 0.25s ease; pointer-events:none;
  }
  .zip-item:hover {
    background:rgba(255,255,255,0.055);
    border-color:rgba(108,126,248,0.32);
    transform:translateY(-2px) scale(1.01);
    box-shadow:0 8px 26px rgba(0,0,0,0.22), 0 0 22px rgba(108,126,248,0.09);
  }
  .zip-item:hover::before { opacity:1; }

  .chevron { transition:transform 0.32s cubic-bezier(0.34,1.56,0.64,1); display:inline-block; }
  .chevron.open { transform:rotate(180deg); }

  .spin {
    display:inline-block; width:14px; height:14px; border-radius:50%;
    border:2px solid rgba(108,126,248,0.2); border-top-color:#6c7ef8;
    animation:spin 0.75s cubic-bezier(0.4,0,0.6,1) infinite; flex-shrink:0;
  }

  input[type="checkbox"] { width:16px; height:16px; accent-color:#6c7ef8; cursor:pointer; transition:transform 0.18s cubic-bezier(0.34,1.56,0.64,1); }
  input[type="checkbox"]:hover { transform:scale(1.12); }

  .status-banner {
    display:flex; align-items:flex-start; gap:12px; padding:14px 18px;
    border-radius:12px; margin-bottom:12px;
    animation:scaleIn 0.38s cubic-bezier(0.22,1,0.36,1) both;
  }
  .status-banner.error   { background:rgba(248,113,113,0.09); border:1px solid rgba(248,113,113,0.25); }
  .status-banner.warning { background:rgba(245,158,11,0.09);  border:1px solid rgba(245,158,11,0.25); }
  .status-banner.success { background:rgba(52,211,153,0.09);  border:1px solid rgba(52,211,153,0.25); }
  .status-banner.info    { background:rgba(108,126,248,0.09); border:1px solid rgba(108,126,248,0.25); }
  .status-banner-icon    { font-size:18px; flex-shrink:0; margin-top:1px; }
  .status-banner-body    { flex:1; min-width:0; }
  .status-banner-title   { font-size:13.5px; font-weight:700; margin-bottom:2px; }
  .status-banner-desc    { font-size:12.5px; line-height:1.55; opacity:0.85; }
  .status-banner-dismiss { background:none; border:none; cursor:pointer; color:rgba(255,255,255,0.3); font-size:18px; padding:0; line-height:1; flex-shrink:0; transition:color 0.2s ease, transform 0.2s ease; }
  .status-banner-dismiss:hover { color:rgba(255,255,255,0.7); transform:scale(1.12); }

  .nav-user-btn {
    display:flex; align-items:center; gap:8px; padding:6px 12px 6px 6px;
    border-radius:10px; background:rgba(255,255,255,0.04); border:1px solid rgba(255,255,255,0.07);
    cursor:pointer; transition:all 0.25s cubic-bezier(0.34,1.56,0.64,1);
  }
  .nav-user-btn:hover {
    background:rgba(255,255,255,0.08);
    border-color:rgba(108,126,248,0.32);
    transform:scale(1.02);
    box-shadow:0 4px 16px rgba(108,126,248,0.13);
  }

  .citation-row { display:flex; align-items:flex-start; gap:10px; padding:10px 0; border-bottom:1px solid rgba(255,255,255,0.05); transition:background 0.15s ease; }
  .citation-row:last-child { border-bottom:none; }
  .citation-num { font-family:'JetBrains Mono',monospace; font-size:10.5px; color:rgba(108,126,248,0.7); font-weight:600; flex-shrink:0; padding-top:2px; }
`;

// ─── ERROR SANITIZER ─────────────────────────────────────────────────────────
// Exported so PipelineQueue imports the same logic — one source of truth.
//
// Any backend error containing database URIs, Python tracebacks, API keys,
// Google auth exceptions, internal service names, etc. is blocked here and
// replaced with a generic safe message before it ever reaches the UI.

const INFRA_PATTERNS = [
  // Database
  /mongo/i, /pymongo/i, /motor/i, /\bdb\b/i, /collection/i,
  /uri.*(not found|invalid|missing)/i, /connection string/i,
  // Auth / credentials
  /api[_\s-]?key/i, /secret/i, /credential/i, /service.?account/i,
  /oauth/i, /google\.auth/i, /bearer/i, /token.*(invalid|expired|missing)/i,
  // Network / infra
  /econnrefused/i, /connection\s*(refused|timed out|reset)/i,
  /\bsocket\b/i, /\bdns\b/i, /\bssl\b/i, /certificate/i,
  // Python internals / stack traces
  /traceback/i, /file ".*\.py"/i, /line \d+, in /i,
  /exception:/i, /\.py", line/i,
  // Cloud / env
  /\b(gcp|aws|azure)\b/i, /bucket|blob|\bs3\b/i,
  /\bredis\b/i, /\bcelery\b/i, /environment variable/i, /\.env\b/i,
  // OpenAI / LLM internals
  /openai\.error/i, /invalid_api_key/i, /rate_limit_exceeded/i,
  // Catch-all infra phrases
  /internal server error/i, /service unavailable/i,
];

/**
 * Returns null if the raw string contains infra-sensitive content.
 * Returns the trimmed string if it's safe to show to the user.
 * Exported — imported by PipelineQueue.
 */
export function sanitizeErrorMessage(raw = "") {
  const str = String(raw).trim();
  if (!str) return null;
  // Block Python file paths
  if (str.includes('File "/') || str.includes('.py", line')) return null;
  // Block anything suspiciously long — likely a stack trace dump
  if (str.length > 280) return null;
  // Block known infra patterns
  if (INFRA_PATTERNS.some(p => p.test(str))) return null;
  return str;
}

/** Safe copy keyed by error category. Exported — used in PipelineQueue. */
export const SAFE_ERROR_MESSAGES = {
  quota:     { type: "warning", title: "Usage limit reached",         description: "You've used all your credits for this period. Upgrade your plan or wait for your quota to reset." },
  rateLimit: { type: "warning", title: "You already have 3 generations running",  description: "ZenithPost runs up to 3 content generations at a time per account. Wait for one to complete, then start your next one — your quota is safe and has not been used." },
  auth:      { type: "error",   title: "Authentication error",        description: "Your session may have expired. Try logging out and back in." },
  server:    { type: "error",   title: "Something went wrong",        description: "Our pipeline ran into an issue. Your quota has not been charged. Please try again in a moment." },
  network:   { type: "error",   title: "Connection issue",            description: "We couldn't reach the server. Check your connection and try again." },
  notFound:  { type: "error",   title: "Resource not found",          description: "The requested resource could not be found. Please try again." },
  default:   { type: "error",   title: "Something went wrong",        description: "An unexpected error occurred. Please try again. If this keeps happening, contact support." },
};

/** Maps any thrown API error to a safe { type, title, description } object. */
function parseApiError(e, context = "") {
  const status    = e?.status ?? e?.response?.status;
  const rawDetail = String(
    e?.detail ?? e?.response?.data?.detail ?? e?.response?.data?.message ?? e?.message ?? ""
  );

  if (status === 403) {
    const d = rawDetail.toLowerCase();
    if (d.includes("trend"))
      return { ...SAFE_ERROR_MESSAGES.quota, title: "Trend search limit reached",      description: "You've used all your trend searches for this period. Upgrade or wait for your quota to reset." };
    if (d.includes("content") || d.includes("generation"))
      return { ...SAFE_ERROR_MESSAGES.quota, title: "Content generation limit reached", description: "You've used all your content generation credits for this period." };
    return SAFE_ERROR_MESSAGES.quota;
  }
  if (status === 429) return SAFE_ERROR_MESSAGES.rateLimit;
  if (status === 401) return SAFE_ERROR_MESSAGES.auth;
  if (status === 404) return SAFE_ERROR_MESSAGES.notFound;
  if (!status && (e?.code === "ERR_NETWORK" || e?.code === "ECONNABORTED"))
    return SAFE_ERROR_MESSAGES.network;

  // Only pass through the detail text if it doesn't contain infra info
  const safeDetail = sanitizeErrorMessage(rawDetail);
  if (safeDetail) {
    return {
      type:        status >= 500 ? "error" : "warning",
      title:       `${context || "Request"} failed`,
      description: safeDetail,
    };
  }

  // Infra leak detected — use generic server error copy
  return SAFE_ERROR_MESSAGES.server;
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function PlatformTabPanel({ children, value, index }) {
  return <>{value === index && <Box sx={{ mt: 2.5 }}>{children}</Box>}</>;
}

function scoreColor(v) {
  const n = parseFloat(v);
  if (isNaN(n)) return D.textMuted;
  if (n >= 7)   return D.accent;
  if (n >= 4.5) return D.warn;
  return D.danger;
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button onClick={handleCopy} title="Copy post" style={{
      position: "absolute", top: 10, right: 10,
      background: copied ? "rgba(52,211,153,0.12)" : "rgba(255,255,255,0.05)",
      border: `1px solid ${copied ? "rgba(52,211,153,0.3)" : "rgba(255,255,255,0.1)"}`,
      borderRadius: 7, padding: "5px 10px", cursor: "pointer",
      color: copied ? "#34d399" : "#8b91a8", fontSize: 12, fontWeight: 600,
      fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 5,
    }}>
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
}

function ScoreBadge({ value }) {
  if (value === null || value === undefined || value === "")
    return <span style={{ color: D.textMuted, fontSize: 12 }}>—</span>;
  const c       = scoreColor(value);
  const display = typeof value === "number" ? value.toFixed(1) : value;
  return <span className="score-badge" style={{ background: `${c}18`, color: c, border: `1px solid ${c}35` }}>{display}</span>;
}

function normaliseCitation(c) {
  if (typeof c === "string") return { title: c, url: "", source: "", date: "", key_finding: "" };
  if (!c || typeof c !== "object") return null;
  const pick = (...keys) => { for (const k of keys) { const v = c[k]; if (v && typeof v === "string" && v.trim()) return v.trim(); } return ""; };
  return {
    title:       pick("title","TITLE","name","NAME"),
    url:         pick("url","URL","link","LINK","href","HREF"),
    source:      pick("source","SOURCE","AUTHOR/OUTLET","author","AUTHOR"),
    date:        pick("date","DATE","published","PUBLISHED"),
    key_finding: pick("key_finding","KEY FINDING","ONE-LINE HOOK","finding","FINDING","summary"),
  };
}

function UsageMeter({ label, remaining, total, color = D.accent }) {
  const pct            = total > 0 ? Math.round((remaining / total) * 100) : 0;
  const low            = pct <= 20;
  const effectiveColor = low ? D.danger : color;
  return (
    <div className="stat-card">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1.5 }}>
        <Typography sx={{ fontSize: "11px", fontFamily: D.fontMono, color: D.textMuted, letterSpacing: "0.07em", textTransform: "uppercase" }}>{label}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          {low && <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: D.danger, animation: "pulse 1.5s ease infinite" }} />}
          <Typography sx={{ fontSize: "11.5px", fontFamily: D.fontMono, color: effectiveColor, fontWeight: 600 }}>{pct}% left</Typography>
        </Box>
      </Box>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 1.5 }}>
        <Typography sx={{ fontSize: "28px", fontWeight: 700, color: D.textPrimary, fontFamily: D.fontDisplay, lineHeight: 1 }}>{remaining.toLocaleString()}</Typography>
        <Typography sx={{ fontSize: "13px", color: D.textSec }}>/ {total.toLocaleString()}</Typography>
      </Box>
      <Box sx={{ height: 4, borderRadius: 99, background: "rgba(255,255,255,0.06)", overflow: "hidden" }}>
        <Box sx={{ height: "100%", width: `${pct}%`, borderRadius: 99, background: effectiveColor, transition: "width 0.9s cubic-bezier(0.22,1,0.36,1)", boxShadow: `0 0 8px ${effectiveColor}55` }} />
      </Box>
      {low && remaining === 0 && <Typography sx={{ fontSize: "11px", fontFamily: D.fontMono, color: D.danger, mt: 1 }}>Limit reached — upgrade to continue</Typography>}
    </div>
  );
}

function StatusBanner({ type = "error", title, description, onDismiss }) {
  const config = {
    error:   { icon: "⚠️", titleColor: D.danger,  descColor: "#f8a0a0" },
    warning: { icon: "🔔", titleColor: D.warn,    descColor: "#fcd08a" },
    success: { icon: "🎉", titleColor: D.success, descColor: "#7de8c3" },
    info:    { icon: "ℹ️", titleColor: D.accent,  descColor: "#a8b4fc" },
  };
  const c = config[type] || config.error;
  return (
    <div className={`status-banner ${type}`}>
      <span className="status-banner-icon">{c.icon}</span>
      <div className="status-banner-body">
        {title       && <div className="status-banner-title" style={{ color: c.titleColor }}>{title}</div>}
        {description && <div className="status-banner-desc"  style={{ color: c.descColor  }}>{description}</div>}
      </div>
      {onDismiss && <button className="status-banner-dismiss" onClick={onDismiss}>×</button>}
    </div>
  );
}

function downloadAllZips(zipUrls) {
  zipUrls.forEach((z, i) => {
    setTimeout(() => {
      const a = document.createElement("a");
      a.href = z.url;
      a.download = `${z.topic.replace(/[^a-z0-9]/gi, "_")}.zip`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }, i * 400);
  });
}

const PLATFORMS = [
  { label: "Facebook",   icon: "📘", key: "facebook"    },
  { label: "Instagram",  icon: "📸", key: "instagram"   },
  { label: "LinkedIn",   icon: "💼", key: "linkedin"    },
  { label: "Blogs",      icon: "📝", key: "blogs"       },
  { label: "Newsletter", icon: "📬", key: "newsletters" },
  { label: "Citations",  icon: "🔗", key: "citations"   },
];

function buildResultTabs(r, hasMedium, hasCI) {
  const tabs = [
    { label: "Facebook",   icon: "📘", key: "facebook",           type: "posts"       },
    { label: "Instagram",  icon: "📸", key: "instagram",          type: "posts"       },
    { label: "LinkedIn",   icon: "💼", key: "linkedin",           type: "posts"       },
    { label: "Twitter",    icon: "𝕏",  key: "twitter",            type: "posts"       },
    { label: "Blogs",      icon: "📝", key: "blogs",              type: "blogs"       },
    { label: "Newsletter", icon: "📬", key: "newsletters",        type: "newsletters" },
    { label: "Citations",  icon: "🔗", key: "citations",          type: "citations"   },
  ];
  if (hasMedium && r.medium_articles?.length > 0)
    tabs.push({ label: "Medium",     icon: "✍️",  key: "medium_articles",    type: "posts"      });
  if (hasCI && r.strategy_brief)
    tabs.push({ label: "Strategy",   icon: "📋",  key: "strategy_brief",     type: "brief"      });
  // Hooks are shown inline below FB/IG posts — no separate tab needed
  if (hasCI && r.competitor_analysis)
    tabs.push({ label: "Competitor", icon: "🔍",  key: "competitor_analysis",type: "competitor" });
  return tabs;
}

const TREND_COLS = [
  { id: "select",               label: "",             width: 44  },
  { id: "topic",                label: "Topic",        width: 220 },
  { id: "why_trending",         label: "Why Trending", width: 280 },
  { id: "breakout_score",       label: "Breakout",     width: 88  },
  { id: "breakout_label",       label: "Label",        width: 150 },
  { id: "predicted_7d_growth",  label: "7d Growth",    width: 88  },
  { id: "velocity",             label: "Velocity",     width: 82  },
  { id: "virality",             label: "Virality",     width: 82  },
  { id: "freshness",            label: "Freshness",    width: 82  },
  { id: "emotional_intensity",  label: "Emotional",    width: 88  },
  { id: "creator_opportunity",  label: "Creator",      width: 82  },
  { id: "monetization",         label: "Monetize",     width: 82  },
  { id: "saturation",           label: "Saturation",   width: 88  },
  { id: "opportunity_gap",      label: "Opp. Gap",     width: 82  },
  { id: "boring",               label: "Boring",       width: 74  },
  { id: "longevity_prediction", label: "Longevity",    width: 100 },
];

function ZenithpostLogo({ size = "md" }) {
  const sizes = { sm: { box: 26, font: 13, textSize: 13 }, md: { box: 32, font: 16, textSize: 15 }, lg: { box: 44, font: 22, textSize: 20 } };
  const s = sizes[size] || sizes.md;
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
      <Box sx={{ width: s.box, height: s.box, borderRadius: "9px", background: `linear-gradient(135deg, ${D.accent} 0%, ${D.purple} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: D.fontDisplay, fontWeight: 800, fontSize: s.font, color: "white", flexShrink: 0, animation: "float 4s ease-in-out infinite, logoGlow 3s ease infinite", boxShadow: "0 4px 18px rgba(108,126,248,0.38)" }}>Z</Box>
      <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: s.textSize, color: D.textPrimary, lineHeight: 1, letterSpacing: "-0.01em" }}>
        zenithpost<span style={{ color: D.accent }}>.ai</span>
      </Typography>
    </Box>
  );
}

function CitationsTab({ citations = [] }) {
  if (!citations || citations.length === 0)
    return <Typography sx={{ fontSize: "13.5px", color: D.textSec, mt: 2 }}>No sources available.</Typography>;

  const normalised    = citations.map(normaliseCitation).filter(Boolean);
  const hasAnyContent = normalised.some(c => c.title || c.url || c.source || c.key_finding);

  if (!hasAnyContent) {
    return (
      <Box sx={{ mt: 1 }}>
        <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 1.5 }}>Sources · {citations.length} (raw)</Typography>
        {citations.map((c, ci) => (
          <div key={ci} className="citation-row">
            <span className="citation-num">[{ci + 1}]</span>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: "12px", color: D.textSec, fontFamily: D.fontMono, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                {typeof c === "string" ? c : JSON.stringify(c, null, 2)}
              </Typography>
            </Box>
          </div>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 1 }}>
      <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 2 }}>Sources · {normalised.length}</Typography>
      {normalised.map((c, ci) => (
        <div key={ci} className="citation-row">
          <span className="citation-num">[{ci + 1}]</span>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {c.title       && <Typography sx={{ fontSize: "13px", fontWeight: 600, color: D.textPrimary, mb: 0.4, lineHeight: 1.4 }}>{c.title}</Typography>}
            {(c.source || c.date) && (
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 0.4, flexWrap: "wrap" }}>
                {c.source && <Typography sx={{ fontSize: "11.5px", color: D.textMuted, fontFamily: D.fontMono }}>{c.source}</Typography>}
                {c.date   && <Typography sx={{ fontSize: "11px",   color: D.textMuted, fontFamily: D.fontMono }}>· {c.date}</Typography>}
              </Box>
            )}
            {c.key_finding && <Typography sx={{ fontSize: "12.5px", color: D.textSec, mb: 0.6, lineHeight: 1.6, fontStyle: "italic" }}>{c.key_finding}</Typography>}
            {c.url && (
              <Typography component="a" href={c.url} target="_blank" rel="noopener noreferrer"
                sx={{ fontSize: "11.5px", fontFamily: D.fontMono, color: D.accent, textDecoration: "none", wordBreak: "break-all", "&:hover": { textDecoration: "underline" } }}>
                {c.url}
              </Typography>
            )}
          </Box>
        </div>
      ))}
    </Box>
  );
}

const CAL_PM = {
  linkedin:  { icon: "💼", color: "#0A66C2", bg: "rgba(10,102,194,0.10)",  border: "rgba(10,102,194,0.25)" },
  instagram: { icon: "📸", color: "#E1306C", bg: "rgba(225,48,108,0.10)",  border: "rgba(225,48,108,0.25)" },
  facebook:  { icon: "📘", color: "#1877F2", bg: "rgba(24,119,242,0.10)",  border: "rgba(24,119,242,0.25)" },
};

const _TZ_ABBR = { CDT:"CT",CST:"CT",EDT:"ET",EST:"ET",PDT:"PT",PST:"PT",MDT:"MT",MST:"MT",AKDT:"AKT",AKST:"AKT",HST:"HT",HDT:"HT",AST:"AT",ADT:"AT" };

function _validTz(tz) {
  // Returns tz if the browser accepts it, otherwise falls back to Chicago
  try { new Intl.DateTimeFormat("en-US", { timeZone: tz }); return tz; }
  catch { return "America/Chicago"; }
}

function tzAbbr(ianaTimezone) {
  try {
    const raw = new Intl.DateTimeFormat("en-US", { timeZone: ianaTimezone, timeZoneName: "short" })
      .formatToParts(new Date()).find(p => p.type === "timeZoneName")?.value || "CT";
    return _TZ_ABBR[raw] || raw;
  } catch { return "CT"; }
}

function calSlotDate(slot) {
  const tz = _validTz(slot.timezone || "America/Chicago");
  const dayNames = ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"];
  const now = new Date();
  const todayIdx = now.getDay();
  const targetIdx = dayNames.indexOf(slot.day_of_week);
  const weekOffset = (slot.week_number - 1) * 7;
  const daysUntil = (targetIdx - todayIdx + 7) % 7 || 7;
  const target = new Date(now);
  target.setDate(now.getDate() + weekOffset + daysUntil);
  try {
    const dateStr = target.toLocaleDateString("en-CA", { timeZone: tz });
    const label = new Date(`${dateStr}T${slot.time}:00`).toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true, timeZone: tz,
    });
    return `${label} ${tzAbbr(tz)}`;
  } catch {
    // Final fallback — render without timezone formatting
    return `${slot.day_of_week} · ${slot.time} CT`;
  }
}

function HomeCalendar({ calendar, topic, imageUrls }) {
  const navigate = useNavigate();
  const weeks = [1, 2, 3, 4].filter(w => calendar.some(s => s.week_number === w));
  const [activeWeek, setActiveWeek] = useState(weeks[0] || 1);
  const [expanded, setExpanded] = useState(true);

  const weekSlots = calendar.filter(s => s.week_number === activeWeek);

  return (
    <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${D.border}` }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5, flexWrap: "wrap", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }} onClick={() => setExpanded(e => !e)}>
          <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: 14.5, color: D.textPrimary }}>
            📅 Editorial Calendar
          </Typography>
          <Typography sx={{ fontFamily: D.fontMono, fontSize: 11, color: D.textMuted }}>
            {calendar.length} posts · 30-day schedule
          </Typography>
          <Typography sx={{ fontSize: 11, color: D.textMuted }}>{expanded ? "▲" : "▼"}</Typography>
        </Box>
        <Button variant="contained" size="small"
          onClick={() => navigate("/autopost", { state: { calendar, topic, imageUrls } })}
          sx={{ background: "linear-gradient(135deg,#6c7ef8,#a37bf8) !important", color: "white !important", fontWeight: 700, fontSize: 12, px: 2, py: 0.8, whiteSpace: "nowrap" }}>
          ⚡ Schedule All {calendar.length} Posts
        </Button>
      </Box>

      {expanded && (
        <>
          {/* Week selector */}
          <Box sx={{ display: "flex", gap: 1, mb: 2, flexWrap: "wrap" }}>
            {weeks.map(w => (
              <Box
                key={w}
                onClick={() => setActiveWeek(w)}
                sx={{
                  px: 1.5, py: 0.5, borderRadius: "7px", cursor: "pointer", fontSize: 12, fontFamily: D.fontMono,
                  fontWeight: activeWeek === w ? 700 : 500,
                  background: activeWeek === w ? `${D.accent}20` : "rgba(255,255,255,0.04)",
                  border: `1px solid ${activeWeek === w ? D.accent + "50" : D.border}`,
                  color: activeWeek === w ? D.accent : D.textMuted,
                }}
              >
                Week {w} · {calendar.filter(s => s.week_number === w).length} posts
              </Box>
            ))}
          </Box>

          {/* Slots */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {weekSlots.map((slot, si) => {
              const pm = CAL_PM[slot.platform] || { icon: "📣", color: D.accent, bg: `${D.accent}10`, border: `${D.accent}30` };
              return (
                <Box key={si} sx={{ p: "10px 14px", background: "rgba(255,255,255,0.02)", border: `1px solid ${D.border}`, borderLeft: `3px solid ${pm.color}`, borderRadius: "9px" }}>
                  {/* Row 1: platform + content type + date/time */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.75, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <span style={{ fontSize: 13 }}>{pm.icon}</span>
                      <Typography sx={{ fontFamily: D.fontMono, fontSize: 11, color: pm.color, fontWeight: 700 }}>{slot.platform}</Typography>
                    </Box>
                    {slot.content_type && (
                      <Typography sx={{ fontFamily: D.fontMono, fontSize: 10, color: D.textMuted, background: "rgba(255,255,255,0.05)", px: 0.75, py: 0.25, borderRadius: "4px" }}>
                        {slot.content_type}
                      </Typography>
                    )}
                    <Typography sx={{ fontFamily: D.fontMono, fontSize: 11, color: D.textSec }}>
                      📆 {calSlotDate(slot)}
                    </Typography>
                  </Box>
                  {/* Row 2: content preview */}
                  <Typography sx={{ fontSize: 12.5, color: "#c0c7de", lineHeight: 1.6, mb: slot.reasoning ? 0.75 : 0, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {slot.content}
                  </Typography>
                  {/* Row 3: why */}
                  {slot.reasoning && (
                    <Box sx={{ display: "flex", gap: 0.75, alignItems: "flex-start", mt: 0.5 }}>
                      <span style={{ fontSize: 11, flexShrink: 0, marginTop: 1 }}>💡</span>
                      <Typography sx={{ fontSize: 11, color: D.textMuted, fontStyle: "italic", lineHeight: 1.55 }}>
                        {slot.reasoning}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}

const QUEUE_TTL_MS = 4 * 60 * 60 * 1000; // 4 hours
const TERMINAL_STATUSES = ["complete", "failed", "canceled"];

function filterStaleQueueTasks(tasks) {
  if (!Array.isArray(tasks)) return tasks;
  const now = Date.now();
  return tasks.filter(t => {
    if (!TERMINAL_STATUSES.includes(t.status)) return true;
    if (!t.created_at) return false;
    return now - new Date(t.created_at).getTime() < QUEUE_TTL_MS;
  });
}

function usePersistedState(key, defaultValue) {
  const [state, setState] = useState(() => {
    try {
      const stored = sessionStorage.getItem(key);
      if (!stored) return defaultValue;
      const parsed = JSON.parse(stored);
      return key === "zp_queue" ? filterStaleQueueTasks(parsed) : parsed;
    }
    catch { return defaultValue; }
  });
  const setPersistedState = useCallback((value) => {
    setState((prev) => {
      const next = typeof value === "function" ? value(prev) : value;
      try { sessionStorage.setItem(key, JSON.stringify(next)); } catch {}
      return next;
    });
  }, [key]);
  return [state, setPersistedState];
}

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function App() {
  const navigate = useNavigate();

  const [loadingTrends,   setLoadingTrends]   = useState(false);
  const [loadingPipeline, setLoadingPipeline] = useState(false);
  const [currentTopic,    setCurrentTopic]    = useState("");
  const [currentIdx,      setCurrentIdx]      = useState(0);
  const [errors,          setErrors]          = useState([]);
  const [userMenuAnchor,  setUserMenuAnchor]  = useState(null);
  const [mobileNavOpen,   setMobileNavOpen]   = useState(false);
  const [autoPosting,     setAutoPosting]     = useState(false);
  const [hasCampaignMode,        setHasCampaignMode]        = useState(false);
  const [hasEbook,               setHasEbook]               = useState(false);
  const [hasEmailSequences,      setHasEmailSequences]      = useState(false);
  const [hasMedium,              setHasMedium]              = useState(false);
  const [hasContentIntelligence, setHasContentIntelligence] = useState(false);
  const [hasVideo,               setHasVideo]               = useState(false);
  const [hasCompliance,          setHasCompliance]          = useState(false);
  const isMobile = useMediaQuery('(max-width:768px)');
  const [usage, setUsage] = useState({
    remaining_trend_searches: 0, remaining_content_generations: 0,
    total_trend_searches: 0,     total_content_generations: 0,
  });

  const [domain,     setDomain]     = usePersistedState("zp_domain",  "");
  const [topics,     setTopics]     = usePersistedState("zp_topics",  []);
  const MAX_SELECTIONS = 3;
  const [selected,   setSelected]   = usePersistedState("zp_selected",[]);
  const [results,    setResults]    = usePersistedState("zp_results", []);
  const [tabMap,     setTabMap]     = usePersistedState("zp_tabMap",  {});
  const [openMap,    setOpenMap]    = usePersistedState("zp_openMap", {});
  const [zipUrls,    setZipUrls]    = usePersistedState("zp_zipUrls", []);
  const [queueTasks, setQueueTasks] = usePersistedState("zp_queue",   []);
  const [loading,    setLoading]    = useState(true);

  const allDone = !loadingPipeline && zipUrls.length === selected.length && selected.length > 0;
  const activeJobCount = queueTasks.filter(t => t.source !== "topic_studio" && (t.status === "queued" || t.status === "running")).length;
  const atConcurrentLimit = activeJobCount >= MAX_SELECTIONS;
  const selectionLocked = loadingPipeline || atConcurrentLimit;
  const [showLockedNudge, setShowLockedNudge] = useState(false);

  const handleLockedClick = () => {
    setShowLockedNudge(true);
    setTimeout(() => setShowLockedNudge(false), 3000);
  };
  const email   = getEmailFromToken();

  const addError = (errObj) => {
    const id = Date.now() + Math.random();
    setErrors(prev => [...prev, { id, ...errObj }]);
    if (errObj.type === "success" || errObj.type === "info")
      setTimeout(() => setErrors(prev => prev.filter(e => e.id !== id)), 6000);
  };
  const dismissError = (id) => setErrors(prev => prev.filter(e => e.id !== id));
  const clearErrors  = ()   => setErrors([]);

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("email");
    localStorage.removeItem("userId");
    clearSession();
    navigate("/login");
  };

  // ── On mount: auth check + fetch usage + RESTORE TASKS FROM MONGODB ───────
  // Tasks are stored in MongoDB so they survive logout, browser close, refresh.
  // We merge fetched tasks with sessionStorage — sessionStorage wins on overlap
  // (it has fresher status from the last active session).
  // PipelineQueue's useEffect watches `tasks` and auto-resumes polling for any
  // restored tasks that are still queued/running.
  useEffect(() => {
    const token = localStorage.getItem("auth_token");
    if (!token) { navigate("/login", { replace: true }); return; }

    (async () => {
      try {
        setLoading(true);

        // Fire all requests in parallel — don't let any block the others
        const [usageRes, tasksRes, meRes] = await Promise.allSettled([
          api.get("/dashboard/usage"),
          api.get("/pipeline/tasks"),   // { tasks: [...] } from your MongoDB
          api.get("/auth/me"),
        ]);

        if (meRes.status === "fulfilled") {
          setAutoPosting(!!meRes.value.data?.auto_posting);
          setHasCampaignMode(!!meRes.value.data?.has_campaign_mode);
          setHasEbook(!!meRes.value.data?.has_ebook);
          setHasEmailSequences(!!meRes.value.data?.has_email_sequences);
          setHasMedium(!!meRes.value.data?.has_medium);
          setHasContentIntelligence(!!meRes.value.data?.has_content_intelligence);
          setHasVideo(!!meRes.value.data?.has_video);
          setHasCompliance(!!meRes.value.data?.has_compliance);
        }

        if (usageRes.status === "fulfilled") {
          setUsage(usageRes.value.data);
        } else {
          addError(parseApiError(usageRes.reason, "Usage fetch"));
        }

        if (tasksRes.status === "fulfilled") {
          const fetched = tasksRes.value.data?.tasks ?? [];
          if (fetched.length > 0) {
            setQueueTasks(prev => {
              const existingMap = new Map(prev.map(t => [t.task_id, t]));

              // Update any sessionStorage tasks whose DB status is now terminal.
              // sessionStorage wins for active tasks (fresher step_label/pct),
              // but the DB is authoritative for complete/failed/canceled.
              const updatedPrev = prev.map(t => {
                const db = fetched.find(f => (f.task_id ?? String(f._id)) === t.task_id);
                if (!db) return t;
                const terminalStatuses = ["complete", "failed", "canceled"];
                if (terminalStatuses.includes(db.status) && !terminalStatuses.includes(t.status)) {
                  return {
                    ...t,
                    status:     db.status,
                    step_label: db.step_label ?? t.step_label,
                    pct:        db.status === "complete" ? 100 : t.pct,
                  };
                }
                return t;
              });

              // Add tasks from DB that aren't in sessionStorage at all.
              // Skip old completed tasks — they belong in ContentHistory, not
              // the queue. Only surface completed tasks from the last 4 hours
              // (covers the logout-mid-run scenario) plus any active tasks.
              const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;
              const now = Date.now();
              const incoming = fetched
                .filter(t => {
                  if (existingMap.has(t.task_id ?? String(t._id))) return false;
                  const terminal = ["complete", "failed", "canceled"].includes(t.status);
                  if (terminal) {
                    const age = t.created_at ? now - new Date(t.created_at).getTime() : Infinity;
                    return age < FOUR_HOURS_MS;
                  }
                  return true; // always show queued/running/canceling tasks
                })
                .map(t => ({
                  task_id:    t.task_id    ?? String(t._id),
                  topic:      t.topic      ?? "Unknown topic",
                  status:     t.status     ?? "queued",
                  source:     t.source     ?? "trend",
                  step:       t.step       ?? null,
                  step_label: t.step_label ?? null,
                  created_at: t.created_at ?? null,
                  // Complete tasks show 100%; others start at 5 so bar isn't empty
                  pct:        t.status === "complete" ? 100 : (t.pct ?? 5),
                  // Never surface raw error strings from the DB —
                  // they may contain Mongo/Google/traceback text from a past crash
                  error:      null,
                  duplicate:  t.duplicate ?? false,
                }));
              return [...updatedPrev, ...incoming];
            });
          }
        }
        // If /pipeline/tasks fails, we silently fall back to sessionStorage.
        // No error banner — losing the restored list is non-critical.

      } catch (e) {
        addError(parseApiError(e, "Dashboard load"));
      } finally {
        setLoading(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTrends = async () => {
    setLoadingTrends(true);
    setTopics([]);
    setSelected([]);
    clearErrors();
    try {
      const res = await api.get(`/trends?domain=${encodeURIComponent(domain)}`);
      setTopics(res.data);
    } catch (e) {
      addError(parseApiError(e, "Trend search"));
    } finally {
      setLoadingTrends(false);
    }
  };

  const run = async () => {
    if (loadingPipeline || !selected?.length) return;
    setLoadingPipeline(true);
    clearErrors();

    const enqueued = await Promise.all(
      selected.map(async (topic) => {
        try {
          const res  = await api.post("/pipeline/run-download", { topic, source: "trend", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago" });
          const data = res.data;
          return {
            task_id:    data.task_id,
            topic:      data.topic    ?? topic,
            status:     data.status   ?? "queued",
            step:       null,
            step_label: null,
            pct:        5,
            error:      null,
            duplicate:  data.duplicate ?? false,
          };
        } catch (e) {
          addError(parseApiError(e, `"${topic}" queue`));
          return null;
        }
      }),
    );

    const newTasks = enqueued.filter(Boolean);
    if (newTasks.length > 0) setQueueTasks(prev => [...prev, ...newTasks]);
    setLoadingPipeline(false);
  };


  const handleTaskComplete = async (result) => {
  if (!result) return;

  const topic =
    result.topic ||
    queueTasks.find(t => t.task_id === result.task_id)?.topic ||
    (result.run_slug
      ? result.run_slug
          .replace(/_\d{8}_\d{6}$/, "")
          .replace(/_/g, " ")
          .trim()
      : "Generated Content");

  const id = result.run_slug || result.task_id || String(Date.now());

  // Only fetch calendar when auto-posting is enabled
  let editorial_calendar = [];
  if (autoPosting) {
    editorial_calendar = result.editorial_calendar ?? [];
    if (editorial_calendar.length === 0 && result.task_id) {
      try {
        const calRes = await api.get(`/social/calendar/${result.task_id}`);
        editorial_calendar = calRes.data?.calendar ?? [];
      } catch (_) {}
    }
  }

  setResults(prev => {
    if (prev.some(r => r.id === id)) return prev;
    return [...prev, {
      id,
      topic,
      task_id:                     result.task_id,
      facebook:                    result.facebook                    ?? [],
      instagram:                   result.instagram                   ?? [],
      linkedin:                    result.linkedin                    ?? [],
      twitter:                     result.twitter                     ?? [],
      blogs:                       result.blogs                       ?? [],
      newsletters:                 result.newsletters                 ?? [],
      citations:                   result.citations                   ?? [],
      editorial_calendar,
      image_urls:                  result.image_urls                  ?? [],
      medium_articles:             result.medium_articles             ?? [],
      hook_variants:               result.hook_variants               ?? {},
      newsletter_subject_variants: result.newsletter_subject_variants ?? {},
      strategy_brief:              result.strategy_brief              ?? "",
      competitor_analysis:         result.competitor_analysis         ?? "",
      competitor_insights:         result.competitor_insights         ?? "",
    }];
  });

  setTabMap(prev  => ({ ...prev, [id]: 0    }));
  setOpenMap(prev => ({ ...prev, [id]: true }));

  setZipUrls(prev => {
    if (prev.some(z => z.url === result.zip_url)) return prev;
    return [...prev, { topic, url: result.zip_url }];
  });
};

  // Sanitize task-level errors before showing a banner.
  // The raw error from a crashed pipeline often contains Mongo/Google/traceback text.
  const handleTaskFailed = (task, rawError) => {
    const safe = sanitizeErrorMessage(String(rawError ?? ""));
    addError({
      type:        "error",
      title:       `"${task.topic}" failed`,
      description: safe ?? SAFE_ERROR_MESSAGES.server.description,
    });
  };

  const clearSession = () => {
    ["zp_domain","zp_topics","zp_selected","zp_results",
     "zp_tabMap","zp_openMap","zp_zipUrls","zp_queue"].forEach(k => sessionStorage.removeItem(k));
    setDomain(""); setTopics([]); setSelected([]);
    setResults([]); setTabMap({}); setOpenMap({});
    setZipUrls([]); setQueueTasks([]);
  };

  // ── Loading screen ─────────────────────────────────────────────────────────
  if (loading) return (
    <ThemeProvider theme={muiTheme}><CssBaseline /><style>{G}</style>
      <style>{`
        @keyframes orbitRing { to { transform: rotate(360deg); } }
        @keyframes orbitDot  { 0%,100%{ transform: scale(1); opacity:1; } 50%{ transform: scale(1.4); opacity:0.7; } }
        @keyframes loadFadeUp { from{ opacity:0; transform:translateY(16px); filter:blur(6px); } to{ opacity:1; transform:translateY(0); filter:blur(0); } }
        @keyframes loadPulseRing { 0%{ box-shadow:0 0 0 0 rgba(108,126,248,0.5); } 70%{ box-shadow:0 0 0 18px rgba(108,126,248,0); } 100%{ box-shadow:0 0 0 0 rgba(108,126,248,0); } }
        @keyframes loadShimmer { 0%{ opacity:0.4; } 50%{ opacity:1; } 100%{ opacity:0.4; } }
        .load-screen-bg { position:fixed; inset:0; background:#0d0e14; overflow:hidden; }
        .load-screen-bg::before { content:''; position:absolute; width:600px; height:600px; border-radius:50%; background:radial-gradient(circle, rgba(108,126,248,0.09) 0%, transparent 70%); top:-150px; left:-100px; animation:orbFloat1 20s ease-in-out infinite; }
        .load-screen-bg::after  { content:''; position:absolute; width:500px; height:500px; border-radius:50%; background:radial-gradient(circle, rgba(52,211,153,0.05) 0%, transparent 70%); bottom:-120px; right:-80px; animation:orbFloat2 26s ease-in-out infinite; }
      `}</style>
      <div className="load-screen-bg" />
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 3.5, position: "relative", zIndex: 1 }}>
        {/* Orbital loader ring */}
        <Box sx={{ position: "relative", width: 80, height: 80, display: "flex", alignItems: "center", justifyContent: "center", animation: "loadFadeUp 0.6s ease both" }}>
          {/* Outer orbit */}
          <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", border: "1.5px solid rgba(108,126,248,0.15)", animation: "loadPulseRing 2s ease infinite" }} />
          <Box sx={{ position: "absolute", inset: 0, borderRadius: "50%", border: "1.5px solid transparent", borderTopColor: D.accent, borderRightColor: `${D.accent}55`, animation: "orbitRing 1.2s cubic-bezier(0.4,0,0.6,1) infinite" }} />
          {/* Inner orbit */}
          <Box sx={{ position: "absolute", inset: 14, borderRadius: "50%", border: "1.5px solid transparent", borderTopColor: D.purple, borderLeftColor: `${D.purple}44`, animation: "orbitRing 0.8s cubic-bezier(0.4,0,0.6,1) infinite reverse" }} />
          {/* Center logo mark */}
          <Box sx={{ width: 32, height: 32, borderRadius: "8px", background: `linear-gradient(135deg, ${D.accent} 0%, ${D.purple} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: D.fontDisplay, fontWeight: 800, fontSize: 16, color: "white", boxShadow: "0 4px 18px rgba(108,126,248,0.45)", animation: "logoGlow 2s ease infinite" }}>Z</Box>
        </Box>
        {/* Brand + text */}
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, animation: "loadFadeUp 0.6s 0.15s ease both" }}>
          <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "18px", color: D.textPrimary, letterSpacing: "-0.01em" }}>
            zenithpost<span style={{ color: D.accent }}>.ai</span>
          </Typography>
          {/* Progress dots */}
          <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
            {[0,1,2,3].map(i => (
              <Box key={i} sx={{ width: 5, height: 5, borderRadius: "50%", background: i < 2 ? D.accent : "rgba(108,126,248,0.25)", animation: `loadShimmer 1.6s ${i*0.25}s ease infinite` }} />
            ))}
          </Box>
          <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, letterSpacing: "0.14em", textTransform: "uppercase", animation: "loadShimmer 2s 0.3s ease infinite" }}>Loading dashboard</Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );

  return (
    <ThemeProvider theme={muiTheme}><CssBaseline /><style>{G}</style>
      <div className="page-glow" />
      <div className="page-glow-mid" />

      {/* NAV */}
      <AppBar position="sticky" elevation={0} sx={{ background: "rgba(13,14,20,0.92)", backdropFilter: "blur(20px)", borderBottom: `1px solid ${D.border}`, zIndex: 100 }}>
        <Toolbar sx={{ gap: 2, minHeight: "62px !important", px: { xs: 2, md: 3 } }}>
          <Box sx={{ display: { xs: "flex", md: "none" }, mr: 0.5 }}>
            <IconButton onClick={() => setMobileNavOpen(true)} sx={{ color: D.textSec, border: `1px solid ${D.border}`, borderRadius: "8px", padding: "6px 10px", fontSize: "18px" }}>☰</IconButton>
          </Box>
          <ZenithpostLogo size="md" />
          <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 0.5, ml: 3 }}>
            <Button onClick={() => navigate("/dashboard")} sx={{ px: 2, py: 0.75, borderRadius: "8px", background: `${D.accent}15`, border: `1px solid ${D.accentBorder}`, textTransform: "none", fontFamily: D.fontBody, fontSize: "13px", fontWeight: 600, color: D.accent, "&:hover": { background: `${D.accent}25` } }}>Dashboard</Button>
            <Button onClick={() => navigate("/history")} sx={{ px: 2, py: 0.75, borderRadius: "8px", background: `${D.purple}15`, border: `1px solid ${D.purple}40`, textTransform: "none", fontFamily: D.fontBody, fontSize: "13px", fontWeight: 600, color: D.purple, "&:hover": { background: `${D.purple}25` } }}>History</Button>
            {autoPosting && (
              <Button onClick={() => navigate("/autopost")} sx={{ px: 2, py: 0.75, borderRadius: "8px", background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.3)", textTransform: "none", fontFamily: D.fontBody, fontSize: "13px", fontWeight: 600, color: D.success, "&:hover": { background: "rgba(52,211,153,0.18)" } }}>📡 Auto Post</Button>
            )}
            {hasCampaignMode && (
              <Button onClick={() => navigate("/campaign")} sx={{ px: 2, py: 0.75, borderRadius: "8px", background: "rgba(108,126,248,0.1)", border: "1px solid rgba(108,126,248,0.3)", textTransform: "none", fontFamily: D.fontBody, fontSize: "13px", fontWeight: 600, color: "#6c7ef8", "&:hover": { background: "rgba(108,126,248,0.18)" } }}>⚡ Freestyle Run</Button>
            )}
            {hasMedium && (
              <Button onClick={() => navigate("/files")} sx={{ px: 2, py: 0.75, borderRadius: "8px", background: "rgba(163,123,248,0.1)", border: "1px solid rgba(163,123,248,0.3)", textTransform: "none", fontFamily: D.fontBody, fontSize: "13px", fontWeight: 600, color: D.purple, "&:hover": { background: "rgba(163,123,248,0.18)" } }}>✍️ Medium</Button>
            )}
            {hasEbook && (
              <Button onClick={() => navigate("/ebook")} sx={{ px: 2, py: 0.75, borderRadius: "8px", background: "rgba(52,211,153,0.1)", border: "1px solid rgba(52,211,153,0.3)", textTransform: "none", fontFamily: D.fontBody, fontSize: "13px", fontWeight: 600, color: D.success, "&:hover": { background: "rgba(52,211,153,0.18)" } }}>📚 eBook</Button>
            )}
            {hasEmailSequences && (
              <Button onClick={() => navigate("/email-sequences")} sx={{ px: 2, py: 0.75, borderRadius: "8px", background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.3)", textTransform: "none", fontFamily: D.fontBody, fontSize: "13px", fontWeight: 600, color: "#f59e0b", "&:hover": { background: "rgba(245,158,11,0.18)" } }}>✉️ Email Sequences</Button>
            )}
            {hasVideo && (
              <Button onClick={() => navigate("/video")} sx={{ px: 2, py: 0.75, borderRadius: "8px", background: "rgba(124,106,255,0.1)", border: "1px solid rgba(124,106,255,0.3)", textTransform: "none", fontFamily: D.fontBody, fontSize: "13px", fontWeight: 600, color: D.accent, "&:hover": { background: "rgba(124,106,255,0.18)" } }}>🎬 Video</Button>
            )}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", gap: 1, px: 1.5, py: 0.6, borderRadius: "8px", background: `${D.success}12`, border: `1px solid ${D.success}30` }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: D.success, animation: "pulse 2.5s ease infinite" }} />
            <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.success, fontWeight: 600, letterSpacing: "0.06em" }}>ACTIVE</Typography>
          </Box>
          <Box className="nav-user-btn" onClick={e => setUserMenuAnchor(e.currentTarget)}>
            <Avatar sx={{ width: 28, height: 28, fontSize: 12, fontWeight: 700, background: `linear-gradient(135deg, ${D.accent}, ${D.purple})`, fontFamily: D.fontDisplay }}>
              {(email[0] || "U").toUpperCase()}
            </Avatar>
            <Typography sx={{ fontFamily: D.fontBody, fontSize: "13px", color: D.textSec, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: { xs: "none", sm: "block" } }}>{email || "User"}</Typography>
            <Typography sx={{ color: D.textMuted, fontSize: 12 }}>▾</Typography>
          </Box>
          <Menu anchorEl={userMenuAnchor} open={Boolean(userMenuAnchor)} onClose={() => setUserMenuAnchor(null)}
            transformOrigin={{ horizontal: "right", vertical: "top" }} anchorOrigin={{ horizontal: "right", vertical: "bottom" }} PaperProps={{ sx: { mt: 1, minWidth: 200 } }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${D.border}` }}>
              <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.25 }}>Signed in as</Typography>
              <Typography sx={{ fontSize: "13px", color: D.textPrimary, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>{email}</Typography>
            </Box>
            <MenuItem onClick={() => { navigate("/account");                  setUserMenuAnchor(null); }} sx={{ gap: 1.5, py: 1.25 }}><span>👤</span><span>My Account</span></MenuItem>
            <MenuItem onClick={() => { navigate("/account?tab=subscription"); setUserMenuAnchor(null); }} sx={{ gap: 1.5, py: 1.25 }}><span>💳</span><span>Subscription</span></MenuItem>
            <MenuItem onClick={() => { navigate("/account?tab=usage");        setUserMenuAnchor(null); }} sx={{ gap: 1.5, py: 1.25 }}><span>📊</span><span>Usage & Top-ups</span></MenuItem>
            <Divider sx={{ borderColor: D.border }} />
            <MenuItem onClick={handleLogout} sx={{ gap: 1.5, py: 1.25, color: D.danger }}><span>🚪</span><span>Log out</span></MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Mobile navigation drawer */}
      <Drawer anchor="left" open={mobileNavOpen} onClose={() => setMobileNavOpen(false)}
        PaperProps={{ sx: { width: 280, background: "#13151e", borderRight: `1px solid ${D.border}` } }}>
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${D.border}` }}>
          <ZenithpostLogo size="md" />
        </Box>
        <List sx={{ pt: 1 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { navigate("/dashboard"); setMobileNavOpen(false); }}
              sx={{ borderRadius: "8px", mx: 1, mb: 0.5, color: D.accent, fontWeight: 600 }}>
              <ListItemText primary="Dashboard" primaryTypographyProps={{ fontFamily: D.fontBody, fontWeight: 600, fontSize: "15px" }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { navigate("/history"); setMobileNavOpen(false); }}
              sx={{ borderRadius: "8px", mx: 1, mb: 0.5, color: D.purple }}>
              <ListItemText primary="History" primaryTypographyProps={{ fontFamily: D.fontBody, fontWeight: 600, fontSize: "15px" }} />
            </ListItemButton>
          </ListItem>
          {autoPosting && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { navigate("/autopost"); setMobileNavOpen(false); }}
                sx={{ borderRadius: "8px", mx: 1, mb: 0.5, color: D.success }}>
                <ListItemText primary="📡 Auto Post" primaryTypographyProps={{ fontFamily: D.fontBody, fontWeight: 600, fontSize: "15px" }} />
              </ListItemButton>
            </ListItem>
          )}
          {hasMedium && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { navigate("/files"); setMobileNavOpen(false); }}
                sx={{ borderRadius: "8px", mx: 1, mb: 0.5, color: D.purple }}>
                <ListItemText primary="✍️ Medium" primaryTypographyProps={{ fontFamily: D.fontBody, fontWeight: 600, fontSize: "15px" }} />
              </ListItemButton>
            </ListItem>
          )}
          {hasEbook && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { navigate("/ebook"); setMobileNavOpen(false); }}
                sx={{ borderRadius: "8px", mx: 1, mb: 0.5, color: D.success }}>
                <ListItemText primary="📚 eBook Wizard" primaryTypographyProps={{ fontFamily: D.fontBody, fontWeight: 600, fontSize: "15px" }} />
              </ListItemButton>
            </ListItem>
          )}
          {hasEmailSequences && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { navigate("/email-sequences"); setMobileNavOpen(false); }}
                sx={{ borderRadius: "8px", mx: 1, mb: 0.5, color: "#f59e0b" }}>
                <ListItemText primary="✉️ Email Sequences" primaryTypographyProps={{ fontFamily: D.fontBody, fontWeight: 600, fontSize: "15px" }} />
              </ListItemButton>
            </ListItem>
          )}
          {hasVideo && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { navigate("/video"); setMobileNavOpen(false); }}
                sx={{ borderRadius: "8px", mx: 1, mb: 0.5, color: D.accent }}>
                <ListItemText primary="🎬 Video Request" primaryTypographyProps={{ fontFamily: D.fontBody, fontWeight: 600, fontSize: "15px" }} />
              </ListItemButton>
            </ListItem>
          )}
          {hasVideo && (
            <ListItem disablePadding>
              <ListItemButton onClick={() => { navigate("/my-videos"); setMobileNavOpen(false); }}
                sx={{ borderRadius: "8px", mx: 1, mb: 0.5, color: D.accent }}>
                <ListItemText primary="🎬 My Videos" primaryTypographyProps={{ fontFamily: D.fontBody, fontWeight: 600, fontSize: "15px" }} />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem disablePadding>
            <ListItemButton onClick={() => { navigate("/account"); setMobileNavOpen(false); }}
              sx={{ borderRadius: "8px", mx: 1, mb: 0.5 }}>
              <ListItemText primary="My Account" primaryTypographyProps={{ fontFamily: D.fontBody, fontSize: "15px", color: D.textSec }} />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton onClick={() => { navigate("/account?tab=subscription"); setMobileNavOpen(false); }}
              sx={{ borderRadius: "8px", mx: 1, mb: 0.5 }}>
              <ListItemText primary="Subscription" primaryTypographyProps={{ fontFamily: D.fontBody, fontSize: "15px", color: D.textSec }} />
            </ListItemButton>
          </ListItem>
        </List>
        <Box sx={{ mt: "auto", p: 2.5, borderTop: `1px solid ${D.border}` }}>
          <Button fullWidth onClick={handleLogout} sx={{ color: D.danger, border: `1px solid rgba(248,113,113,0.2)`, borderRadius: "9px", fontFamily: D.fontBody, fontWeight: 600, textTransform: "none" }}>
            Log out
          </Button>
        </Box>
      </Drawer>

      <Container maxWidth="xl" sx={{ py: 5, position: "relative", zIndex: 1 }}>

        {/* Hero */}
        <Box className="fade-up" sx={{ mb: 5 }}>
          <Box sx={{ background: "linear-gradient(135deg, rgba(108,126,248,0.08) 0%, rgba(163,123,248,0.05) 50%, rgba(52,211,153,0.04) 100%)", border: `1px solid rgba(108,126,248,0.15)`, borderRadius: "20px", p: { xs: 3, md: 4 }, mb: 4, position: "relative", overflow: "hidden" }}>
            <Box sx={{ position: "absolute", top: -30, right: -30, width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(108,126,248,0.08) 0%, transparent 70%)", pointerEvents: "none" }} />
            <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.textMuted, letterSpacing: "0.1em", textTransform: "uppercase", mb: 1.5 }}>Content Command Center</Typography>
            <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: { xs: "24px", md: "32px" }, color: D.textPrimary, lineHeight: 1.15, mb: 1.5 }}>
              Discover trends. Generate content.<br />
              <span style={{ background: `linear-gradient(135deg, ${D.accent}, ${D.purple})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Ship what works.</span>
            </Typography>
            <Typography sx={{ fontSize: "14.5px", color: D.textSec, maxWidth: 480, lineHeight: 1.7 }}>
              Find trending topics in your domain, analyze virality signals, and generate platform-optimized content in one workflow.
            </Typography>
          </Box>
        </Box>

        {/* Usage */}
        <Box className="fade-up fade-up-1" sx={{ mb: 5 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Typography sx={{ fontSize: "11px", fontFamily: D.fontMono, color: D.textMuted, letterSpacing: "0.08em", textTransform: "uppercase" }}>Account Usage</Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button size="small" onClick={() => navigate("/account?tab=usage")}
                sx={{ fontSize: "12px", color: D.accent, fontFamily: D.fontBody, textTransform: "none", p: "4px 10px", borderRadius: "7px", background: D.accentSoft, border: `1px solid ${D.accentBorder}`, "&:hover": { background: `${D.accent}20` } }}>Top up →</Button>
              {results.length > 0 && (
                <Button size="small" onClick={clearSession}
                  sx={{ fontSize: "12px", color: D.textMuted, fontFamily: D.fontBody, p: "4px 10px", borderRadius: "7px", background: "rgba(255,255,255,0.04)", border: `1px solid ${D.border}`, "&:hover": { color: D.danger, borderColor: "rgba(248,113,113,0.3)" } }}>
                  Clear results
                </Button>
              )}
            </Box>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={3}><UsageMeter label="Trend Searches"      remaining={usage.remaining_trend_searches}      total={usage.total_trend_searches}      color={D.accent}  /></Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}><UsageMeter label="Content Generations" remaining={usage.remaining_content_generations} total={usage.total_content_generations} color={D.success} /></Grid>
          </Grid>
        </Box>

        {/* Banners */}
        {errors.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {errors.map(err => <StatusBanner key={err.id} type={err.type} title={err.title} description={err.description} onDismiss={() => dismissError(err.id)} />)}
          </Box>
        )}

        {/* Domain search */}
        <Box className="fade-up fade-up-2" sx={{ mb: 4 }}>
          <Card sx={{ p: { xs: 2.5, md: 3.5 } }}>
            <Typography sx={{ fontSize: "11px", fontFamily: D.fontMono, color: D.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", mb: 2 }}>Step 1 — Define your domain</Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flexDirection: { xs: "column", sm: "row" } }}>
              <Box sx={{ flex: 1, minWidth: { xs: "100%", sm: 260 }, position: "relative" }}>
                <TextField fullWidth placeholder="e.g. AI healthcare, fintech, sustainable fashion…" value={domain}
                  onChange={e => { if (e.target.value.length <= 160) setDomain(e.target.value); }}
                  onKeyDown={e => e.key === "Enter" && domain && !loadingTrends && !loadingPipeline && loadTrends()}
                  disabled={loadingPipeline || loadingTrends}
                  inputProps={{ maxLength: 150 }}
                  sx={{ width: "100%" }} />
                {domain.length > 100 && (
                  <Typography sx={{ position: "absolute", right: 12, bottom: -18, fontSize: "10px", fontFamily: D.fontMono, color: domain.length > 145 ? "#f87171" : D.textMuted }}>
                    {domain.length}/150
                  </Typography>
                )}
              </Box>
              <Button variant="contained" onClick={loadTrends}
                disabled={!domain || loadingTrends || loadingPipeline || usage.remaining_trend_searches === 0}
                sx={{ px: 3.5, height: 52, minWidth: { xs: "100%", sm: 190 }, background: `linear-gradient(135deg, ${D.accent}, ${D.purple}) !important`, color: "white", fontWeight: 700, fontSize: "14px", "&:hover": { boxShadow: "0 4px 20px rgba(108,126,248,0.4) !important" }, "&:disabled": { opacity: 0.45 } }}
                startIcon={loadingTrends ? <span className="spin" /> : <span>🔍</span>}>
                {loadingTrends ? "Scanning…" : usage.remaining_trend_searches === 0 ? "Limit Reached" : "Find Trending Topics"}
              </Button>
            </Box>
            {loadingTrends && (
              <Box sx={{ mt: 2.5 }}>
                <div className="progress-shimmer" />
                <Typography sx={{ fontSize: "11.5px", fontFamily: D.fontMono, color: D.accent, mt: 1 }}>Analyzing trends for "{domain}"…</Typography>
              </Box>
            )}
          </Card>
        </Box>

        {/* Topics table */}
        {topics.length > 0 && (
          <Box className="fade-up" sx={{ mb: 4 }}>
            <Card sx={{ overflow: "hidden" }}>
              {/* ── Card header: title + run button ── */}
              <Box sx={{ px: 3, pt: 3, pb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, borderBottom: `1px solid ${D.border}` }}>
                <Box>
                  <Typography sx={{ fontSize: "11px", fontFamily: D.fontMono, color: D.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", mb: 0.75 }}>Step 2 — Select topics</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "16px", color: D.textPrimary }}>Trending Topics</Typography>
                    <Chip label={`${topics.length} found`} size="small" sx={{ background: `${D.accent}18 !important`, color: `${D.accent} !important`, border: `1px solid ${D.accentBorder} !important` }} />
                    {selected.length > 0 && <Chip label={`${selected.length} of ${MAX_SELECTIONS} selected`} size="small" sx={{ background: `${D.success}15 !important`, color: `${D.success} !important`, border: "1px solid rgba(52,211,153,0.25) !important" }} />}
                  </Box>
                </Box>
                <Button variant="contained" onClick={run}
                  disabled={selected.length === 0 || selectionLocked || usage.remaining_content_generations === 0}
                  startIcon={loadingPipeline ? <span className="spin" style={{ borderTopColor: "white" }} /> : <span>🚀</span>}
                  sx={{ flexShrink: 0, px: 3, py: 1.2, background: "linear-gradient(135deg,#34d399,#22b67e) !important", color: "#0d1409 !important", fontWeight: 700, fontSize: "13.5px", "&:hover": { boxShadow: "0 4px 16px rgba(52,211,153,0.3) !important" }, "&:disabled": { opacity: 0.45 } }}>
                  {loadingPipeline ? `Generating ${currentIdx}/${selected.length}…` : usage.remaining_content_generations === 0 ? "Generation Limit Reached" : "Run Content Pipeline"}
                </Button>
              </Box>

              {/* ── Guidance strip — sits between header and topic list ── */}
              <Box sx={{ px: 3, py: 1.25, borderBottom: `1px solid ${D.border}`, minHeight: 40, display: "flex", alignItems: "center" }}>
                {showLockedNudge ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.25 }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>⏳</span>
                    <Typography sx={{ fontSize: 12.5, color: "#f59e0b", lineHeight: 1.5 }}>
                      Generations are running — selection unlocks automatically as each one finishes.
                    </Typography>
                  </Box>
                ) : atConcurrentLimit ? (
                  <Typography sx={{ fontSize: 12.5, color: D.textMuted, lineHeight: 1.5 }}>
                    <span style={{ color: "#a5b4fc", fontWeight: 600 }}>{activeJobCount} generation{activeJobCount > 1 ? "s" : ""} in progress</span> — click any topic below to see how to add more.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                    <Typography sx={{ fontSize: 12, color: selected.length === MAX_SELECTIONS ? "#f59e0b" : D.textMuted }}>
                      {selected.length === 0 && "Select up to 3 topics to generate in parallel"}
                      {selected.length > 0 && selected.length < MAX_SELECTIONS && `${selected.length} of ${MAX_SELECTIONS} selected — you can add ${MAX_SELECTIONS - selected.length} more`}
                      {selected.length === MAX_SELECTIONS && `${MAX_SELECTIONS} of ${MAX_SELECTIONS} selected — deselect a topic to choose a different one`}
                    </Typography>
                    {selected.length > 0 && (
                      <Typography sx={{ fontSize: 12, color: D.textMuted, flexShrink: 0, ml: 2 }}>
                        {selected.length === 1 ? "Runs immediately" : "All run in parallel"}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
              {isMobile ? (
                <Box sx={{ p: 2 }}>
                  {topics.map(t => (
                    <Box key={t.topic}
                      onClick={() => { if (loadingPipeline) return; const isSelected = selected.includes(t.topic); if (!isSelected && selectionLocked) { handleLockedClick(); return; } setSelected(s => isSelected ? s.filter(x => x !== t.topic) : s.length < MAX_SELECTIONS ? [...s, t.topic] : s); }}
                      sx={{
                        p: 2, mb: 1.5, borderRadius: "12px",
                        cursor: (!selected.includes(t.topic) && selectionLocked) ? "not-allowed" : "pointer",
                        background: selected.includes(t.topic) ? `${D.accent}0d` : "rgba(255,255,255,0.02)",
                        border: `1px solid ${selected.includes(t.topic) ? D.accentBorder : D.border}`,
                        opacity: (!selected.includes(t.topic) && selectionLocked) ? 0.55 : 1,
                        transition: "all 0.2s",
                        "&:hover": { borderColor: (!selected.includes(t.topic) && selectionLocked) ? D.border : "rgba(255,255,255,0.14)" },
                      }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1 }}>
                        <input type="checkbox" checked={selected.includes(t.topic)} disabled={loadingPipeline}
                          onChange={e => { e.stopPropagation(); if (e.target.checked && selectionLocked) { handleLockedClick(); return; } setSelected(s => e.target.checked ? (s.length < MAX_SELECTIONS ? [...s, t.topic] : s) : s.filter(x => x !== t.topic)); }}
                          style={{ marginTop: 3, accentColor: D.accent, flexShrink: 0 }} />
                        <Typography sx={{ fontWeight: 700, fontSize: "14px", color: D.textPrimary, flex: 1, lineHeight: 1.35 }}>{t.topic}</Typography>
                        <ScoreBadge value={t.breakout_score} />
                      </Box>
                      {t.why_trending && (
                        <Typography sx={{ fontSize: "12.5px", color: D.textSec, mb: 1.5, lineHeight: 1.55, pl: "24px" }}>{t.why_trending}</Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", pl: "24px", alignItems: "center" }}>
                        {t.breakout_label && <Chip label={t.breakout_label} size="small" sx={{ background: `${D.accent}12 !important`, color: `${D.accent} !important`, border: `1px solid ${D.accentBorder} !important`, height: "18px !important", fontSize: "10px !important" }} />}
                        {[
                          { l: "7d", v: t.predicted_7d_growth },
                          { l: "Viral", v: t.virality },
                          { l: "Gap", v: t.opportunity_gap },
                          { l: "Velocity", v: t.velocity },
                        ].map(({ l, v }) => v != null && (
                          <Box key={l} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Typography sx={{ fontSize: "10px", color: D.textMuted, fontFamily: D.fontMono }}>{l}:</Typography>
                            <ScoreBadge value={v} />
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ) : (
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table stickyHeader size="small" sx={{ minWidth: 1300, tableLayout: "fixed" }}>
                    <TableHead>
                      <TableRow>{TREND_COLS.map(col => <TableCell key={col.id} sx={{ width: col.width, minWidth: col.width }}>{col.label}</TableCell>)}</TableRow>
                    </TableHead>
                    <TableBody>
                      {topics.map(t => (
                        <TableRow key={t.topic}
                          className={`topic-row${selected.includes(t.topic) ? " selected" : ""}`}
                          sx={{ opacity: (!selected.includes(t.topic) && selectionLocked) ? 0.5 : 1, transition: "opacity 0.2s", cursor: (!selected.includes(t.topic) && selectionLocked) ? "not-allowed !important" : "pointer" }}
                          onClick={() => { if (loadingPipeline) return; const isSelected = selected.includes(t.topic); if (!isSelected && selectionLocked) { handleLockedClick(); return; } setSelected(s => isSelected ? s.filter(x => x !== t.topic) : s.length < MAX_SELECTIONS ? [...s, t.topic] : s); }}>
                          <TableCell onClick={e => e.stopPropagation()} sx={{ verticalAlign: "middle" }}>
                            <input type="checkbox" checked={selected.includes(t.topic)} disabled={loadingPipeline}
                              onChange={e => { if (e.target.checked && selectionLocked) { handleLockedClick(); return; } setSelected(s => e.target.checked ? (s.length < MAX_SELECTIONS ? [...s, t.topic] : s) : s.filter(x => x !== t.topic)); }} />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: `${D.textPrimary} !important`, wordBreak: "break-word", whiteSpace: "normal !important", lineHeight: "1.4 !important", fontSize: "13px !important" }}>{t.topic}</TableCell>
                          <TableCell sx={{ color: "#c0c7de !important", wordBreak: "break-word", whiteSpace: "normal !important", lineHeight: "1.4 !important", fontSize: "12.5px !important" }}>{t.why_trending}</TableCell>
                          <TableCell><ScoreBadge value={t.breakout_score} /></TableCell>
                          <TableCell>{t.breakout_label && <Chip label={t.breakout_label} sx={{ background: `${D.accent}12 !important`, color: `${D.accent} !important`, border: `1px solid ${D.accentBorder} !important`, height: "20px !important", fontSize: "11px !important" }} />}</TableCell>
                          <TableCell><ScoreBadge value={t.predicted_7d_growth} /></TableCell>
                          <TableCell><ScoreBadge value={t.velocity} /></TableCell>
                          <TableCell><ScoreBadge value={t.virality} /></TableCell>
                          <TableCell><ScoreBadge value={t.freshness} /></TableCell>
                          <TableCell><ScoreBadge value={t.emotional_intensity} /></TableCell>
                          <TableCell><ScoreBadge value={t.creator_opportunity} /></TableCell>
                          <TableCell><ScoreBadge value={t.monetization} /></TableCell>
                          <TableCell><ScoreBadge value={t.saturation} /></TableCell>
                          <TableCell><ScoreBadge value={t.opportunity_gap} /></TableCell>
                          <TableCell><ScoreBadge value={t.boring} /></TableCell>
                          <TableCell><ScoreBadge value={t.longevity_prediction} /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Card>
          </Box>
        )}

        {/* ZIP Downloads */}
        {zipUrls.length > 0 && (
          <Box className="fade-up" sx={{ mb: 4 }}>
            <Card sx={{ p: 3 }}>
              <Typography sx={{ fontSize: "11px", fontFamily: D.fontMono, color: D.textMuted, letterSpacing: "0.08em", textTransform: "uppercase", mb: 1.5 }}>Downloads Ready</Typography>
              <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "15px", color: D.textPrimary, mb: 2.5 }}>Generated Content Packages</Typography>
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                {zipUrls.map((z, i) => (
                  <div key={i} className="zip-item" onClick={() => window.open(z.url, "_blank")}>
                    <span>📦</span>
                    <Typography sx={{ fontSize: "13px", fontWeight: 600, color: D.textPrimary }}>{z.topic}</Typography>
                    <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted }}>ZIP</Typography>
                  </div>
                ))}
              </Box>
              {allDone && (
                <Button onClick={() => downloadAllZips(zipUrls)}
                  sx={{ background: `linear-gradient(135deg, ${D.accent}, ${D.purple}) !important`, color: "white", fontWeight: 700, px: 3, py: 1, "&:hover": { boxShadow: "0 4px 16px rgba(108,126,248,0.3) !important" } }}>
                  Download All Packages
                </Button>
              )}
            </Card>
          </Box>
        )}

        {/* Pipeline queue */}
        <PipelineQueue
          tasks={queueTasks}
          setTasks={setQueueTasks}
          onTaskComplete={handleTaskComplete}
          onTaskFailed={handleTaskFailed}
        />

        {/* Results */}
        {results.map((r, idx) => {
          const rTabs    = buildResultTabs(r, hasMedium, hasContentIntelligence);
          const activeTab = tabMap[r.id] ?? 0;
          return (
          <Box key={r.id} className="fade-up" sx={{ mb: 3, animationDelay: `${idx * 0.06}s` }}>
            <Card>
              <CardContent sx={{ p: 0 }}>
                <Box sx={{ px: 3, py: 2.5, display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", borderBottom: openMap[r.id] ? `1px solid ${D.border}` : "none", "&:hover": { background: "rgba(255,255,255,0.01)" }, transition: "background 0.15s" }}
                  onClick={() => setOpenMap(p => ({ ...p, [r.id]: !p[r.id] }))}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: D.success, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "14.5px", color: D.textPrimary }}>{r.topic}</Typography>
                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                      {PLATFORMS.map(p => r[p.key]?.length > 0 && (
                        <Chip key={p.key} label={`${p.icon} ${r[p.key].length}`} size="small" sx={{ background: "rgba(255,255,255,0.06) !important", color: `${D.textSec} !important`, height: "20px !important", fontSize: "11px !important" }} />
                      ))}
                      {hasMedium && r.medium_articles?.length > 0 && <Chip label={`✍️ ${r.medium_articles.length}`} size="small" sx={{ background: "rgba(255,255,255,0.06) !important", color: `${D.textSec} !important`, height: "20px !important", fontSize: "11px !important" }} />}
                      {hasContentIntelligence && r.strategy_brief      && <Chip label="📋 Strategy"   size="small" sx={{ background: "rgba(108,126,248,0.08) !important", color: `${D.accent} !important`, height: "20px !important", fontSize: "11px !important" }} />}
                      {hasContentIntelligence && r.competitor_analysis && <Chip label="🔍 Competitor" size="small" sx={{ background: "rgba(108,126,248,0.08) !important", color: `${D.accent} !important`, height: "20px !important", fontSize: "11px !important" }} />}
                    </Box>
                  </Box>
                  <span className={`chevron${openMap[r.id] ? " open" : ""}`} style={{ color: D.textMuted, fontSize: 18 }}>▾</span>
                </Box>

                <Collapse in={!!openMap[r.id]}>
                  <Box sx={{ px: 3, pb: 3.5 }}>
                    <Tabs value={activeTab} onChange={(_, v) => setTabMap(p => ({ ...p, [r.id]: v }))} variant="scrollable" scrollButtons="auto" sx={{ mt: 2.5, borderBottom: `1px solid ${D.border}`, mb: 0 }}>
                      {rTabs.map((t, i) => (
                        <Tab key={i} label={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <span>{t.icon}</span><span>{t.label}</span>
                            {Array.isArray(r[t.key]) && r[t.key].length > 0 && (
                              <Box sx={{ px: 0.75, py: 0.1, borderRadius: "5px", background: `${D.accent}18`, minWidth: 18, textAlign: "center" }}>
                                <Typography sx={{ fontSize: "10px", color: D.accent, fontFamily: D.fontMono, fontWeight: 600, lineHeight: 1.5 }}>{r[t.key].length}</Typography>
                              </Box>
                            )}
                          </Box>
                        } />
                      ))}
                    </Tabs>

                    {/* Report banners */}
                    <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mt: 1.5, mb: 0.5 }}>
                      <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, display: "flex", alignItems: "center", gap: 0.5 }}>
                        📋 <span>Quality report included in your ZIP — all auto-corrections documented.</span>
                      </Typography>
                      {hasCompliance && (
                        <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", color: D.textMuted, display: "flex", alignItems: "center", gap: 0.5 }}>
                          🛡️ <span>Compliance report included — every post reviewed against industry regulations.</span>
                        </Typography>
                      )}
                    </Box>

                    {rTabs.map((t, ti) => (
                      <PlatformTabPanel key={ti} value={activeTab} index={ti}>

                        {/* ── Simple posts (Facebook, Instagram, LinkedIn, Medium) ── */}
                        {t.type === "posts" && (() => {
                          const HOOKS = [
                            { key: "curiosity", label: "Curiosity Hook", why: "Opens a knowledge gap — reader keeps scrolling to resolve the tension" },
                            { key: "authority", label: "Authority Hook",  why: "Leads with a credibility signal — builds trust before the message lands" },
                            { key: "fomo",      label: "FOMO Hook",       why: "Creates urgency through loss aversion — drives faster action" },
                          ];
                          const platformHooks = hasContentIntelligence && r.hook_variants
                            ? r.hook_variants[t.key] || null
                            : null;
                          return r[t.key]?.length > 0
                            ? r[t.key].map((post, i) => {
                                const hookSet = platformHooks?.[i] || null;
                                return (
                                  <div key={i} className="content-post" style={{ position: "relative" }}>
                                    <span className="post-index">#{String(i+1).padStart(2,"0")}</span>
                                    <Typography sx={{ fontSize: "13.5px", lineHeight: 1.75, pr: 6, whiteSpace: "pre-wrap", color: "#d0d5e8" }}>{post}</Typography>
                                    <CopyButton text={post} />
                                    {hookSet && (
                                      <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
                                        {HOOKS.map(({ key, label, why }) => hookSet[key] && (
                                          <Box key={key} sx={{ px: 1.5, py: 1, background: `${D.accent}08`, borderRadius: "7px", border: `1px solid ${D.accentBorder}`, position: "relative" }}>
                                            <Typography sx={{ fontFamily: D.fontMono, fontSize: "9.5px", color: D.accent, textTransform: "uppercase", letterSpacing: "0.07em" }}>{label}</Typography>
                                            <Typography sx={{ fontSize: "10px", color: D.textMuted, mb: 0.4, fontStyle: "italic" }}>{why}</Typography>
                                            <Typography sx={{ fontSize: "12.5px", color: "#b8c0d8", lineHeight: 1.6, pr: 5 }}>{hookSet[key]}</Typography>
                                            <CopyButton text={hookSet[key]} />
                                          </Box>
                                        ))}
                                      </Box>
                                    )}
                                  </div>
                                );
                              })
                            : <Typography sx={{ fontSize: "13.5px", color: D.textSec, mt: 2 }}>No content generated for {t.label}.</Typography>;
                        })()}

                        {/* ── Blogs ── */}
                        {t.type === "blogs" && (
                          r.blogs?.length > 0
                            ? r.blogs.map((b, i) => (
                                <Card key={i} sx={{ mb: 3, p: 3, background: "rgba(255,255,255,0.02) !important", border: `1px solid ${D.border} !important`, position: "relative" }}>
                                  <CopyButton text={b.content || (typeof b === "string" ? b : JSON.stringify(b))} />
                                  <ReactMarkdown components={{
                                    h1: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: "22px", mb: 2, color: D.textPrimary }}>{children}</Typography>,
                                    h2: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "17px", mt: 3, mb: 1.5, color: D.textPrimary }}>{children}</Typography>,
                                    h3: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "15px", mt: 2, mb: 1, color: D.textPrimary }}>{children}</Typography>,
                                    p:  ({ children }) => <Typography sx={{ fontSize: "14px", lineHeight: 1.8, mb: 1.5, color: "#c0c7de" }}>{children}</Typography>,
                                    strong: ({ children }) => <Box component="span" sx={{ fontWeight: 700, color: D.textPrimary }}>{children}</Box>,
                                    blockquote: ({ children }) => <Box sx={{ borderLeft: `3px solid ${D.accent}`, pl: 2, py: 0.5, my: 2, background: `${D.accent}08`, borderRadius: "0 8px 8px 0" }}><Typography sx={{ fontSize: "14px", fontStyle: "italic", color: "#b8c0d8" }}>{children}</Typography></Box>,
                                    ul: ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
                                    ol: ({ children }) => <Box component="ol" sx={{ pl: 3, mb: 2 }}>{children}</Box>,
                                    li: ({ children }) => <Box component="li" sx={{ mb: 0.5 }}><Typography sx={{ fontSize: "14px", color: "#c0c7de" }}>{children}</Typography></Box>,
                                    hr: () => <Divider sx={{ my: 3, borderColor: D.border }} />,
                                    code: ({ children }) => <Box component="span" sx={{ fontFamily: D.fontMono, background: `${D.accent}12`, px: 0.75, py: 0.25, borderRadius: "4px", fontSize: "0.85em", color: D.accent }}>{children}</Box>,
                                  }}>{b.content || b}</ReactMarkdown>
                                  {hasContentIntelligence && (b.metaTitle || b.metaDesc || b.primaryKeyword) && (
                                    <Box sx={{ mt: 2.5, p: 2, background: `${D.accent}08`, borderRadius: "9px", border: `1px solid ${D.accentBorder}` }}>
                                      <Typography sx={{ fontFamily: D.fontMono, fontSize: "10.5px", fontWeight: 600, color: D.accent, letterSpacing: "0.07em", mb: 1 }}>SEO METADATA</Typography>
                                      {b.metaTitle      && <Typography sx={{ fontSize: "12px", color: D.textSec, mb: 0.4 }}><strong>Title:</strong> {b.metaTitle}</Typography>}
                                      {b.metaDesc       && <Typography sx={{ fontSize: "12px", color: D.textSec, mb: 0.4 }}><strong>Meta Description:</strong> {b.metaDesc}</Typography>}
                                      {b.primaryKeyword  && <Typography sx={{ fontSize: "12px", color: D.textSec, mb: 0.4 }}><strong>Primary Keyword:</strong> {b.primaryKeyword}</Typography>}
                                      {b.relatedKw       && <Typography sx={{ fontSize: "12px", color: D.textSec, mb: 0.4 }}><strong>Related Keywords:</strong> {b.relatedKw}</Typography>}
                                      {b.searchIntent    && <Typography sx={{ fontSize: "12px", color: D.textSec, mb: 0.4 }}><strong>Search Intent:</strong> {b.searchIntent}</Typography>}
                                      {(b.headlineA || b.headlineB) && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: `1px solid ${D.accentBorder}` }}>
                                          <Typography sx={{ fontFamily: D.fontMono, fontSize: "9.5px", color: D.accent, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>Headline Variants</Typography>
                                          {b.headlineA && (
                                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.4 }}>
                                              <Typography sx={{ fontFamily: D.fontMono, fontSize: "9px", color: D.textMuted, minWidth: 80, pt: 0.2 }}>Curiosity Hook</Typography>
                                              <Typography sx={{ fontSize: "12px", color: D.textSec, flex: 1 }}>{b.headlineA}</Typography>
                                              <CopyButton text={b.headlineA} />
                                            </Box>
                                          )}
                                          {b.headlineB && (
                                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                              <Typography sx={{ fontFamily: D.fontMono, fontSize: "9px", color: D.textMuted, minWidth: 80, pt: 0.2 }}>Direct Benefit</Typography>
                                              <Typography sx={{ fontSize: "12px", color: D.textSec, flex: 1 }}>{b.headlineB}</Typography>
                                              <CopyButton text={b.headlineB} />
                                            </Box>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  )}
                                </Card>
                              ))
                            : <Typography sx={{ fontSize: "13.5px", color: D.textSec, mt: 2 }}>No blogs generated.</Typography>
                        )}

                        {/* ── Newsletters ── */}
                        {t.type === "newsletters" && (
                          r.newsletters?.length > 0
                            ? r.newsletters.map((n, i) => (
                                <Card key={i} sx={{ mb: 2, p: 3, background: "rgba(255,255,255,0.02) !important", border: `1px solid ${D.border} !important`, position: "relative" }}>
                                  <CopyButton text={
                                    typeof n === "object"
                                      ? Object.entries(n).filter(([, v]) => v && typeof v === "string").map(([, v]) => v).join("\n\n")
                                      : String(n)
                                  } />
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, flexWrap: "wrap" }}>
                                    <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "15px", color: D.textPrimary, pr: 8 }}>{(n.subject || "").split("\n").find(l => l.trim()) || `Newsletter ${i+1}`}</Typography>
                                  </Box>
                                  {(() => {
                                    const NL_LABELS = { hook: "Opening", body: "Body", reflection: "Reflection", cta: "CTA", ps: "P.S." };
                                    const NL_MD = {
                                      p:      ({ children }) => <Typography sx={{ fontSize: "13.5px", color: "#c0c7de", lineHeight: 1.75, mb: 1 }}>{children}</Typography>,
                                      strong: ({ children }) => <Box component="span" sx={{ fontWeight: 700, color: D.textPrimary }}>{children}</Box>,
                                      em:     ({ children }) => <Box component="span" sx={{ fontStyle: "italic" }}>{children}</Box>,
                                      ul:     ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 1 }}>{children}</Box>,
                                      ol:     ({ children }) => <Box component="ol" sx={{ pl: 3, mb: 1 }}>{children}</Box>,
                                      li:     ({ children }) => <Box component="li" sx={{ mb: 0.4 }}><Typography sx={{ fontSize: "13.5px", color: "#c0c7de" }}>{children}</Typography></Box>,
                                      hr:     () => <Divider sx={{ my: 1.5, borderColor: D.border }} />,
                                    };
                                    if (typeof n !== "object") {
                                      return <Typography sx={{ fontSize: "13.5px", color: "#c0c7de", whiteSpace: "pre-wrap" }}>{String(n)}</Typography>;
                                    }
                                    return Object.entries(n).map(([k, v]) => {
                                      if (k === "subject" || !v || typeof v !== "string") return null;
                                      return (
                                        <Box key={k} sx={{ mb: 1.5, pb: 1.5, borderBottom: `1px solid ${D.border}` }}>
                                          <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", color: D.textMuted, textTransform: "uppercase", letterSpacing: "0.07em", mb: 0.5, fontStyle: "normal" }}>
                                            {NL_LABELS[k] || k}
                                          </Typography>
                                          <ReactMarkdown components={NL_MD}>{v}</ReactMarkdown>
                                        </Box>
                                      );
                                    });
                                  })()}
                                  {hasContentIntelligence && r.newsletter_subject_variants?.[i] && (() => {
                                    const sv = r.newsletter_subject_variants[i];
                                    return (
                                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${D.accentBorder}` }}>
                                        <Typography sx={{ fontFamily: D.fontMono, fontSize: "9.5px", color: D.accent, textTransform: "uppercase", letterSpacing: "0.07em", mb: 1 }}>Subject Line Variants</Typography>
                                        {sv.direct && (
                                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75, mb: 0.5 }}>
                                            <Typography sx={{ fontFamily: D.fontMono, fontSize: "9px", color: D.textMuted, minWidth: 80, pt: 0.2 }}>Direct</Typography>
                                            <Typography sx={{ fontSize: "12.5px", color: "#c0c7de", flex: 1 }}>{sv.direct}</Typography>
                                            <CopyButton text={sv.direct} />
                                          </Box>
                                        )}
                                        {sv.fomo && (
                                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                                            <Typography sx={{ fontFamily: D.fontMono, fontSize: "9px", color: D.textMuted, minWidth: 80, pt: 0.2 }}>FOMO</Typography>
                                            <Typography sx={{ fontSize: "12.5px", color: "#c0c7de", flex: 1 }}>{sv.fomo}</Typography>
                                            <CopyButton text={sv.fomo} />
                                          </Box>
                                        )}
                                      </Box>
                                    );
                                  })()}
                                </Card>
                              ))
                            : <Typography sx={{ fontSize: "13.5px", color: D.textSec, mt: 2 }}>No newsletters generated.</Typography>
                        )}

                        {/* ── Citations ── */}
                        {t.type === "citations" && <CitationsTab citations={r.citations} />}

                        {/* ── Strategy Brief ── */}
                        {t.type === "brief" && (
                          <Box sx={{ position: "relative" }}>
                            <CopyButton text={r.strategy_brief} />
                            <Box sx={{ p: 2.5, background: `${D.accent}06`, borderRadius: "10px", border: `1px solid ${D.accentBorder}`, mt: 2 }}>
                              <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", fontWeight: 700, color: D.accent, letterSpacing: "0.09em", textTransform: "uppercase", mb: 2 }}>Content Strategy Brief</Typography>
                              <ReactMarkdown components={{
                                h1: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 800, fontSize: "18px", mb: 1.5, color: D.textPrimary }}>{children}</Typography>,
                                h2: ({ children }) => <Typography sx={{ fontFamily: D.fontDisplay, fontWeight: 700, fontSize: "15px", mt: 2.5, mb: 1, color: D.textPrimary }}>{children}</Typography>,
                                p:  ({ children }) => <Typography sx={{ fontSize: "13.5px", lineHeight: 1.8, mb: 1.5, color: "#c0c7de" }}>{children}</Typography>,
                                li: ({ children }) => <Box component="li" sx={{ mb: 0.4 }}><Typography sx={{ fontSize: "13.5px", color: "#c0c7de" }}>{children}</Typography></Box>,
                                ul: ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 1.5 }}>{children}</Box>,
                                ol: ({ children }) => <Box component="ol" sx={{ pl: 3, mb: 1.5 }}>{children}</Box>,
                              }}>{r.strategy_brief}</ReactMarkdown>
                            </Box>
                          </Box>
                        )}

                        {/* Hooks are now inline below their posts — no separate tab */}

                        {/* ── Competitor Analysis ── */}
                        {t.type === "competitor" && (
                          <Box sx={{ mt: 2 }}>
                            <Box sx={{ p: 2.5, background: "rgba(255,255,255,0.02)", border: `1px solid ${D.border}`, borderRadius: "10px", position: "relative" }}>
                              <CopyButton text={r.competitor_analysis} />
                              <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", fontWeight: 700, color: D.textMuted, letterSpacing: "0.09em", textTransform: "uppercase", mb: 2 }}>Competitive Landscape Analysis</Typography>
                              <Typography sx={{ fontSize: "13.5px", color: "#c0c7de", whiteSpace: "pre-wrap", lineHeight: 1.75, pr: 5 }}>{r.competitor_analysis}</Typography>
                            </Box>
                            {r.competitor_insights && (
                              <Box sx={{ mt: 2, p: 2.5, background: `${D.accent}06`, border: `1px solid ${D.accentBorder}`, borderRadius: "10px", position: "relative" }}>
                                <CopyButton text={r.competitor_insights} />
                                <Typography sx={{ fontFamily: D.fontMono, fontSize: "10px", fontWeight: 700, color: D.accent, letterSpacing: "0.09em", textTransform: "uppercase", mb: 2 }}>Writer Positioning Instructions</Typography>
                                <Typography sx={{ fontSize: "13.5px", color: "#c0c7de", whiteSpace: "pre-wrap", lineHeight: 1.75, pr: 5 }}>{r.competitor_insights}</Typography>
                              </Box>
                            )}
                          </Box>
                        )}

                      </PlatformTabPanel>
                    ))}

                    {/* Editorial calendar + Schedule All Posts — only when auto-posting is enabled */}
                    {autoPosting && r.editorial_calendar?.length > 0 && (
                      <HomeCalendar calendar={r.editorial_calendar} topic={r.topic} imageUrls={r.image_urls || []} />
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          </Box>
          );
        })}

        {/* Footer */}
        <Box sx={{ mt: 6, pt: 4, borderTop: `1px solid ${D.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <ZenithpostLogo size="sm" />
          <Typography sx={{ fontFamily: D.fontMono, fontSize: "11px", color: D.textMuted }}>Content Command Center · v2.0</Typography>
        </Box>
        <Box sx={{ height: 40 }} />
      </Container>
    </ThemeProvider>
  );
}
